import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from "react";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import { useUndoRedo } from "../../hooks/useUndoRedo";
import {
    PROPERTY_REGISTRY,
    cloneDocument,
    coerceDocument,
    createArc,
    createDefaultPreset,
    createEffect,
    createEllipse,
    createEmptyDocument,
    createGroup,
    createGroupOutlinePreset,
    createHeatZone,
    createLayer,
    createOutline,
    createLine,
    createNode,
    createNodeBrushOptions,
    createNodeInteractOptions,
    createConnectSelectionOptions,
    createMultiNodeBrushOptions,
    createMegaNodeBrushOptions,
    connectionPairsForSelection,
    nodeBrushPositions,
    nodeInteractionMoves,
    nodeConnectPulsePairs,
    createPattern,
    createPolygon,
    createRect,
    createStrokeStyle,
    findNodeNearPosition,
    mirrorPointSets,
    mirrorPositions,
    newId,
    nextCopyLabel,
    regenerateNodeSpawn,
    renderDiagram2DToSvg,
    roundVec2,
} from "../../utils/svgDiagram2DModel";
import type {
    Connection,
    ConnectionPreset,
    ConnectSelectionOptions,
    DiagramArc,
    DiagramDocument,
    DiagramEllipse,
    DiagramHeatZone,
    DiagramLayer,
    DiagramLine,
    DiagramNode,
    DiagramPattern,
    DiagramPolygon,
    DiagramRect,
    DiagramTool,
    Effect,
    EffectKind,
    Fill,
    GroupOutlinePreset,
    GroupProperty,
    GroupPropertyKind,
    GroupToolMode,
    MegaNodeBrushOptions,
    MultiNodeBrushOptions,
    NodeBrushOptions,
    NodeInteractOptions,
    NodeGroup,
    NodeShape,
    SpawnPattern,
    StrokeStyle,
    SymmetryAxis,
    SymmetryState,
    Vec2,
} from "../../utils/svgDiagram2DModel";

const TOOL_ID = "svg-diagram-2d";

/** Node fields that propagate to spawn clones when the template changes. */
const SPAWN_TEMPLATE_APPEARANCE_KEYS = [
    "color",
    "radius",
    "shape",
    "visible",
    "effectIds",
] as const satisfies ReadonlyArray<keyof DiagramNode>;

function hostIdsUsingTemplate(
    nodes: DiagramNode[],
    templateId: string,
): string[] {
    return nodes
        .filter((n) => n.spawn?.templateId === templateId)
        .map((n) => n.id);
}

/** Rebuild spawn clones for each host that still has a pattern. */
function regenerateAllSpawns(nodes: DiagramNode[]): DiagramNode[] {
    let next = nodes;
    for (const host of nodes) {
        if (host.spawn) next = regenerateNodeSpawn(next, host.id);
    }
    return next;
}

/** Collect ids to delete when removing a node (the node + its spawn clones). */
function collectRemovedNodeIds(
    nodes: DiagramNode[],
    nodeId: string,
): Set<string> {
    const removedIds = new Set<string>([nodeId]);
    for (const n of nodes) {
        if (n.spawnedFrom?.hostId === nodeId) removedIds.add(n.id);
    }
    for (const n of nodes) {
        if (n.spawnedFrom && removedIds.has(n.spawnedFrom.hostId)) {
            removedIds.add(n.id);
        }
    }
    return removedIds;
}

function finalizeNodeRemoval(
    prev: DiagramDocument,
    removedIds: Set<string>,
): DiagramDocument {
    let nodes = prev.nodes.filter((n) => !removedIds.has(n.id));
    const clearedHostIds: string[] = [];
    nodes = nodes.map((n) => {
        if (n.spawn?.templateId && removedIds.has(n.spawn.templateId)) {
            clearedHostIds.push(n.id);
            return { ...n, spawn: null };
        }
        return n;
    });
    for (const hostId of clearedHostIds) {
        nodes = regenerateNodeSpawn(nodes, hostId);
    }
    return {
        ...prev,
        nodes,
        connections: prev.connections.filter(
            (c) => !removedIds.has(c.sourceId) && !removedIds.has(c.targetId),
        ),
        groups: prev.groups.map((g) => ({
            ...g,
            nodeIds: g.nodeIds.filter((nid) => !removedIds.has(nid)),
        })),
    };
}

/**
 * Build the connection(s) to add for a source→target pair: the direct link
 * between the two nodes, plus — when symmetry is active — one link per
 * mirrored endpoint pair, but only between counterpart nodes that already
 * exist at the mirrored positions (matched by geometry, no stored link).
 * Any pair of nodes is connectable. Skips connections that already exist.
 * Returns [] when nothing is added. Pure over `nodes`/`connections`, so it
 * runs inside a setDoc updater and is shared by the Connect tool, the node
 * panel, and "Connect selected".
 */
function buildConnectionAdditions(
    nodes: DiagramNode[],
    connections: Connection[],
    sourceId: string,
    targetId: string,
    presetId: string,
    symmetry: SymmetryState,
    center: Vec2,
): Connection[] {
    if (sourceId === targetId) return [];
    const srcNode = nodes.find((n) => n.id === sourceId);
    const tgtNode = nodes.find((n) => n.id === targetId);
    if (!srcNode || !tgtNode) return [];

    const connects = (aId: string, bId: string): boolean =>
        connections.some(
            (c) =>
                (c.sourceId === aId && c.targetId === bId) ||
                (c.sourceId === bId && c.targetId === aId),
        );

    const additions: Connection[] = [];
    const layerOf = (nid: string): string | undefined =>
        nodes.find((n) => n.id === nid)?.layerId;
    const addLink = (aId: string, bId: string) => {
        if (aId === bId) return;
        if (connects(aId, bId)) return;
        // Connections can't span layers: skip a pair whose endpoints live on
        // different layers. The connection rides their shared layer.
        const layerId = layerOf(aId);
        if (!layerId || layerId !== layerOf(bId)) return;
        // Avoid duplicating within this same batch (e.g. a self-symmetric pair).
        if (
            additions.some(
                (c) =>
                    (c.sourceId === aId && c.targetId === bId) ||
                    (c.sourceId === bId && c.targetId === aId),
            )
        ) {
            return;
        }
        additions.push({
            id: newId("conn"),
            layerId,
            sourceId: aId,
            targetId: bId,
            presetId,
        });
    };

    addLink(sourceId, targetId);

    // Under symmetry, also connect counterparts found at the mirrored endpoint
    // positions. Pair them up by mirror index so each symmetric copy of the
    // source links to the matching symmetric copy of the target.
    const srcMirrorPts = mirrorPositions(srcNode.position, symmetry, center);
    const tgtMirrorPts = mirrorPositions(tgtNode.position, symmetry, center);
    const pairs = Math.min(srcMirrorPts.length, tgtMirrorPts.length);
    for (let i = 0; i < pairs; i += 1) {
        const a = findNodeNearPosition(nodes, srcMirrorPts[i]!);
        const b = findNodeNearPosition(nodes, tgtMirrorPts[i]!);
        if (a && b) addLink(a.id, b.id);
    }
    return additions;
}

/**
 * An optional tracing-reference image shown beneath the diagram on the canvas.
 * It is a canvas-only aid: it is never written to the document, persisted, or
 * embedded in the exported SVG. `dataUrl` is the loaded image (a data URL),
 * `naturalWidth`/`naturalHeight` are its intrinsic pixel size (so the viewer can
 * letterbox it into the canvas at the right aspect ratio).
 */
export interface RefImage {
    dataUrl: string;
    naturalWidth: number;
    naturalHeight: number;
    name: string;
}

export type AlignMode =
    | "left"
    | "center-h"
    | "right"
    | "top"
    | "middle-v"
    | "bottom"
    | "distribute-h"
    | "distribute-v";

const DEFAULT_VIEWBOX_W = 2000;
const DEFAULT_VIEWBOX_H = 2828;
const DEFAULT_BACKGROUND = "#ffffff";

/** Pixel offset applied to pasted/duplicated nodes so they don't fully overlap. */
const PASTE_OFFSET = 24;

/** Copied shapes (nodes + the connections among them, plus standalone shapes). */
interface ShapeClipboard {
    nodes: Array<Omit<DiagramNode, "id">>;
    /** Connections among copied nodes, by their index in `nodes`. */
    connections: Array<{
        sourceIndex: number;
        targetIndex: number;
        presetId: string;
    }>;
    /** Standalone ellipses, copied verbatim minus their id. */
    ellipses: Array<Omit<DiagramEllipse, "id">>;
    /** Standalone lines, copied verbatim minus their id. */
    lines: Array<Omit<DiagramLine, "id">>;
    /** Standalone arcs, copied verbatim minus their id. */
    arcs: Array<Omit<DiagramArc, "id">>;
    /** Standalone rects, copied verbatim minus their id. */
    rects: Array<Omit<DiagramRect, "id">>;
    /** Standalone polygons, copied verbatim minus their id. */
    polygons: Array<Omit<DiagramPolygon, "id">>;
}

/** Copied node appearance (color/size/shape/effects) for the style paste shortcut. */
type NodeStyleClipboard = Pick<
    DiagramNode,
    "color" | "radius" | "shape" | "effectIds"
>;

/**
 * Style fields shared by the standalone shapes (ellipse/line/arc/rect/polygon)
 * that the multi-select inspector edits in bulk. `fill` only exists on the
 * fillable shapes (ellipse/rect/polygon) — it's silently skipped for lines/arcs.
 */
export interface ShapeStylePatch {
    color?: string;
    strokeWidth?: number;
    dash?: string;
    strokeStyleId?: string | null;
    fill?: Fill | null;
    visible?: boolean;
}

/**
 * Appearance fields the multi-node panel can apply across a selection at once.
 * The node analogue of `ShapeStylePatch` (nodes have no stroke/fill — their
 * `color` is a marker fill).
 */
export interface NodeStylePatch {
    color?: string;
    radius?: number;
    shape?: NodeShape;
    visible?: boolean;
    heatReactive?: boolean;
}

interface SvgDiagram2DState {
    // Document
    doc: DiagramDocument;
    // Subtool + selection
    tool: DiagramTool;
    setTool: (tool: DiagramTool) => void;
    /** Multi-selection (rectangle select, shift-click, or a single click). */
    selectedNodeIds: string[];
    /** Replace the selection with a single node (or clear it with null). */
    setSelectedNodeId: (id: string | null) => void;
    /** Shift-click: add/remove a single node from the selection. */
    toggleNodeSelection: (id: string) => void;
    /** Rectangle-select result; `additive` merges into the existing selection. */
    selectNodesInRect: (ids: string[], additive: boolean) => void;
    /**
     * Rectangle-select result spanning every shape type together (a mixed
     * marquee over nodes, ellipses, lines, arcs, rects, and polygons).
     * `additive` merges into the existing selection.
     */
    selectInRect: (
        nodeIds: string[],
        ellipseIds: string[],
        lineIds: string[],
        arcIds: string[],
        rectIds: string[],
        polygonIds: string[],
        additive: boolean,
    ) => void;
    clearSelection: () => void;
    /**
     * Connect the selected nodes following `connectSelectionOptions` (pattern +
     * proximity thresholds). Used by the Connect-selection subtool, the toolbar
     * button, and the C shortcut.
     */
    connectSelectedNodes: () => void;
    /** Options driving {@link connectSelectedNodes}. */
    connectSelectionOptions: ConnectSelectionOptions;
    setConnectSelectionOptions: (partial: Partial<ConnectSelectionOptions>) => void;
    /** Remove every connection whose both endpoints are selected. */
    clearConnectionsBetweenSelected: () => void;
    /** Copy the selected nodes (and their internal connections). */
    copySelectedNodes: () => void;
    /** Copy the selected nodes, then delete them. */
    cutSelectedNodes: () => void;
    /** Paste previously copied nodes (offset), selecting the new copies. */
    pasteNodes: () => void;
    /** Copy color/size/shape from the first selected node. */
    copySelectedNodeStyle: () => void;
    /** Apply copied color/size/shape to all selected nodes. */
    pasteNodeStyle: () => void;
    /** Delete all selected nodes. */
    removeSelectedNodes: () => void;
    /**
     * Duplicate a single node in place. Returns the new node's id plus, under
     * active symmetry, the ids of the mirror copies dropped at its mirrored
     * positions (in `mirrorPositions` order, `null` where skipped) so a drag can
     * move the whole symmetric set together. Returns `null` if the node can't be
     * duplicated. `alwaysMirror` forces fresh mirror copies even where the
     * source's existing counterparts already sit (used by alt-drag, where the
     * copy starts atop the source).
     */
    duplicateNode: (
        id: string,
        opts?: { alwaysMirror?: boolean },
    ) => { id: string; mirrorIds: (string | null)[] } | null;
    /** Whether the single-node inspector panel is open. */
    nodePanelOpen: boolean;
    setNodePanelOpen: (open: boolean) => void;
    /**
     * The element currently hovered, by id, for cross-highlighting between the
     * control panel's lists and the canvas. Hovering a canvas shape highlights
     * its panel row and vice versa. Any element id (node / ellipse / line / arc
     * / rect / polygon / group) — ids are unique across types — or null.
     */
    hoveredId: string | null;
    setHoveredId: Dispatch<SetStateAction<string | null>>;
    /** Align (or evenly distribute) the selected nodes. */
    alignSelectedNodes: (mode: AlignMode) => void;
    /** First node clicked while the connect subtool is active. */
    connectSourceId: string | null;
    activePresetId: string | null;
    setActivePresetId: (id: string | null) => void;

    // Node brush (composite subtool): scatter several nodes at once, optionally
    // wiring and/or grouping them — all in one undo step.
    nodeBrushOptions: NodeBrushOptions;
    setNodeBrushOptions: (partial: Partial<NodeBrushOptions>) => void;
    /**
     * Apply one node-brush stroke centered on `center`: drop `options.count`
     * nodes scattered within `options.radius`, then (per the options) connect
     * and/or group them. Everything lands in a single document update / undo
     * step. The dropped nodes become the new selection.
     */
    applyNodeBrush: (center: Vec2, options: NodeBrushOptions) => void;

    // Multi-node brush (composite subtool): stamp several node-brush strokes at
    // once — a "brush of brushes" — all in one undo step.
    multiNodeBrushOptions: MultiNodeBrushOptions;
    setMultiNodeBrushOptions: (partial: Partial<MultiNodeBrushOptions>) => void;
    /**
     * Apply one multi-node-brush stamp centered on `center`: scatter
     * `multiOptions.count` node-brush stroke centers within
     * `multiOptions.spreadRadius`, then run a full node brush (driven by
     * `brushOptions`) at each. All strokes land in a single document update /
     * undo step. Every dropped node becomes the new selection.
     */
    applyMultiNodeBrush: (
        center: Vec2,
        multiOptions: MultiNodeBrushOptions,
        brushOptions: NodeBrushOptions,
    ) => void;

    // Mega-node brush (composite subtool): stamp several multi-node-brush stamps
    // at once — a "brush of multi-node brushes" — all in one undo step.
    megaNodeBrushOptions: MegaNodeBrushOptions;
    setMegaNodeBrushOptions: (partial: Partial<MegaNodeBrushOptions>) => void;
    /**
     * Apply one mega-node-brush stamp centered on `center`: scatter
     * `megaOptions.count` multi-node-brush stamp centers within
     * `megaOptions.spreadRadius`, then run a full multi-node brush (driven by
     * `multiOptions` + `brushOptions`) at each. All strokes land in a single
     * document update / undo step. Every dropped node becomes the new selection.
     */
    applyMegaNodeBrush: (
        center: Vec2,
        megaOptions: MegaNodeBrushOptions,
        multiOptions: MultiNodeBrushOptions,
        brushOptions: NodeBrushOptions,
    ) => void;

    // Node-interact subtool: dispatched on a node, it pulls (attract) or pushes
    // (repel) every other node within range, by a distance falloff. One pulse
    // per click, one undo step.
    nodeInteractOptions: NodeInteractOptions;
    setNodeInteractOptions: (partial: Partial<NodeInteractOptions>) => void;
    /**
     * Apply one node-interact pulse dispatched on `hostId`: move every other
     * (non-clone, unlocked) node within `nodeInteractOptions.radius` toward it
     * (attract) or away from it (repel), scaled by strength and the distance
     * falloff. No-op when nothing is in range. One undo step.
     */
    applyNodeInteraction: (hostId: string) => void;

    // Node CRUD (symmetry-aware)
    addNodeAt: (pos: Vec2) => void;
    updateNode: (id: string, partial: Partial<DiagramNode>) => void;
    /**
     * Continuous move (no history snapshot). Under active symmetry, a node
     * found at the moved node's mirrored position is dragged to track it.
     */
    moveNode: (id: string, pos: Vec2) => void;
    /**
     * Move several nodes at once (drags a whole selection together). Each
     * update may carry `mirrorIds` — its symmetric counterpart ids pinned at
     * drag start (see `findNodeMirrorIds`) — so a continuous drag keeps driving
     * the same counterparts even as the moved node sweeps past others.
     */
    moveNodes: (
        updates: Array<{
            id: string;
            pos: Vec2;
            mirrorIds?: ReadonlyArray<string | null>;
        }>,
    ) => void;
    /**
     * The symmetric counterpart node ids for `id` (in `mirrorPositions` order),
     * matched by geometry against the current document. Captured once at drag
     * start and replayed through `moveNodes`'s `mirrorIds`. `claimed` keeps a
     * node from being grabbed as two movers' counterpart.
     */
    findNodeMirrorIds: (id: string, claimed: Set<string>) => (string | null)[];
    removeNode: (id: string) => void;
    /** Used by the connect subtool's two-click flow. */
    handleNodePick: (id: string) => void;

    // Connections (symmetry-aware)
    addConnection: (
        sourceId: string,
        targetId: string,
        presetId: string,
    ) => void;
    updateConnection: (id: string, partial: Partial<Connection>) => void;
    removeConnection: (id: string) => void;
    /**
     * Insert a node at a connection's midpoint, splitting A→B into A→C and C→B.
     * Selects the new node.
     */
    insertNodeOnConnection: (connectionId: string) => void;

    // Ellipses (standalone shapes)
    /** Selected ellipses (multi-select; first is the one shown in the panel). */
    selectedEllipseIds: string[];
    /** Replace the selection with a single ellipse (or clear it with null). */
    setSelectedEllipseId: (id: string | null) => void;
    /** Shift-click: add/remove a single ellipse from the selection. */
    toggleEllipseSelection: (id: string) => void;
    /** Create an ellipse from its bounding box; returns the new ellipse's id. */
    addEllipse: (center: Vec2, rx: number, ry: number) => string;
    updateEllipse: (id: string, partial: Partial<DiagramEllipse>) => void;
    /** Continuous move of an ellipse center (no history snapshot per step). */
    moveEllipse: (id: string, center: Vec2) => void;
    /**
     * Move several ellipses at once (drags a whole selection together).
     * `rx`/`ry` may be supplied to resize alongside the move (used by the
     * scale tool, which grows the radii by the same factor as the spread).
     */
    moveEllipses: (
        updates: Array<{ id: string; center: Vec2; rx?: number; ry?: number }>,
    ) => void;
    removeEllipse: (id: string) => void;
    /** Delete every selected ellipse. */
    removeSelectedEllipses: () => void;
    /** Duplicate a single ellipse in place; returns the new ellipse's id. */
    duplicateEllipse: (id: string) => string | null;

    // Lines (standalone straight segments)
    /** Selected lines (multi-select; first is the one shown in the panel). */
    selectedLineIds: string[];
    /** Replace the selection with a single line (or clear it with null). */
    setSelectedLineId: (id: string | null) => void;
    /** Shift-click: add/remove a single line from the selection. */
    toggleLineSelection: (id: string) => void;
    /** Create a line from its two endpoints; returns the new line's id. */
    addLine: (start: Vec2, end: Vec2) => string;
    updateLine: (id: string, partial: Partial<DiagramLine>) => void;
    /** Continuous translate of a line (both endpoints; no history snapshot). */
    moveLine: (id: string, start: Vec2, end: Vec2) => void;
    /** Translate several lines at once (drags a whole selection together). */
    moveLines: (updates: Array<{ id: string; start: Vec2; end: Vec2 }>) => void;
    removeLine: (id: string) => void;
    /** Delete every selected line. */
    removeSelectedLines: () => void;
    /** Duplicate a single line in place; returns the new line's id. */
    duplicateLine: (id: string) => string | null;

    // Arcs (standalone two-point arcs)
    /** Selected arcs (multi-select; first is the one shown in the panel). */
    selectedArcIds: string[];
    /** Replace the selection with a single arc (or clear it with null). */
    setSelectedArcId: (id: string | null) => void;
    /** Shift-click: add/remove a single arc from the selection. */
    toggleArcSelection: (id: string) => void;
    /** Create an arc from its two endpoints; returns the new arc's id. */
    addArc: (start: Vec2, end: Vec2) => string;
    updateArc: (id: string, partial: Partial<DiagramArc>) => void;
    /** Continuous translate of an arc (both endpoints; no history snapshot). */
    moveArc: (id: string, start: Vec2, end: Vec2) => void;
    /** Translate several arcs at once (drags a whole selection together). */
    moveArcs: (updates: Array<{ id: string; start: Vec2; end: Vec2 }>) => void;
    removeArc: (id: string) => void;
    /** Delete every selected arc. */
    removeSelectedArcs: () => void;
    /** Duplicate a single arc in place; returns the new arc's id. */
    duplicateArc: (id: string) => string | null;

    // Rectangles (standalone axis-aligned boxes drawn by dragging a box)
    /** Selected rects (multi-select; first is the one shown in the panel). */
    selectedRectIds: string[];
    /** Replace the selection with a single rect (or clear it with null). */
    setSelectedRectId: (id: string | null) => void;
    /** Shift-click: add/remove a single rect from the selection. */
    toggleRectSelection: (id: string) => void;
    /** Create a rect from its two corner points; returns the new rect's id. */
    addRect: (start: Vec2, end: Vec2) => string;
    updateRect: (id: string, partial: Partial<DiagramRect>) => void;
    /** Continuous translate of a rect (both corners; no history snapshot). */
    moveRect: (id: string, start: Vec2, end: Vec2) => void;
    /** Translate several rects at once (drags a whole selection together). */
    moveRects: (updates: Array<{ id: string; start: Vec2; end: Vec2 }>) => void;
    removeRect: (id: string) => void;
    /** Delete every selected rect. */
    removeSelectedRects: () => void;
    /** Duplicate a single rect in place; returns the new rect's id. */
    duplicateRect: (id: string) => string | null;

    // Polygons (standalone closed shapes built from a list of placed vertices)
    /** Selected polygons (multi-select; first is the one shown in the panel). */
    selectedPolygonIds: string[];
    /** Replace the selection with a single polygon (or clear it with null). */
    setSelectedPolygonId: (id: string | null) => void;
    /** Shift-click: add/remove a single polygon from the selection. */
    togglePolygonSelection: (id: string) => void;
    /** Create a polygon from its ordered vertices; returns the new id. */
    addPolygon: (points: Vec2[]) => string;
    updatePolygon: (id: string, partial: Partial<DiagramPolygon>) => void;
    /** Continuous translate of a polygon (all vertices; no history snapshot). */
    movePolygon: (id: string, points: Vec2[]) => void;
    /** Translate several polygons at once (drags a whole selection together). */
    movePolygons: (updates: Array<{ id: string; points: Vec2[] }>) => void;
    removePolygon: (id: string) => void;
    /** Delete every selected polygon. */
    removeSelectedPolygons: () => void;
    /** Duplicate a single polygon in place; returns the new polygon's id. */
    duplicatePolygon: (id: string) => string | null;

    // Heat zones (editor-only rectangular regions; never exported, only recolor
    // heat-reactive elements). Defined like rects: drag two corners.
    /** Selected heat zones (multi-select; first is the one shown in the panel). */
    selectedHeatZoneIds: string[];
    /** Replace the selection with a single heat zone (or clear it with null). */
    setSelectedHeatZoneId: (id: string | null) => void;
    /** Shift-click: add/remove a single heat zone from the selection. */
    toggleHeatZoneSelection: (id: string) => void;
    /** Create a heat zone from its two corner points; returns the new id. */
    addHeatZone: (start: Vec2, end: Vec2) => string;
    updateHeatZone: (id: string, partial: Partial<DiagramHeatZone>) => void;
    /** Continuous translate of a heat zone (both corners; no history snapshot). */
    moveHeatZone: (id: string, start: Vec2, end: Vec2) => void;
    /** Translate several heat zones at once (drags a whole selection together). */
    moveHeatZones: (
        updates: Array<{ id: string; start: Vec2; end: Vec2 }>,
    ) => void;
    removeHeatZone: (id: string) => void;
    /** Delete every selected heat zone. */
    removeSelectedHeatZones: () => void;
    /** Duplicate a single heat zone in place; returns the new zone's id. */
    duplicateHeatZone: (id: string) => string | null;

    // Bulk style edit across a mixed multi-selection of standalone shapes
    // (ellipses, lines, arcs, rects, polygons — not nodes). Applies the given
    // style fields to every selected shape that has them, in one undo step.
    updateSelectedShapesStyle: (partial: ShapeStylePatch) => void;
    // Bulk appearance edit across the selected (non-clone) nodes, applied in
    // one undo step. The node analogue of `updateSelectedShapesStyle`.
    updateSelectedNodesStyle: (partial: NodeStylePatch) => void;

    // Presets
    addPreset: () => void;
    updatePreset: (id: string, partial: Partial<ConnectionPreset>) => void;
    removePreset: (id: string) => void;
    addStrokeStyle: () => void;
    updateStrokeStyle: (id: string, partial: Partial<StrokeStyle>) => void;
    removeStrokeStyle: (id: string) => void;

    // Layers (every element/group lives on one; order = draw order)
    /** The layer newly drawn elements are placed on. */
    activeLayerId: string;
    setActiveLayerId: (id: string) => void;
    /** Append a new layer (becomes active); returns its id. */
    addLayer: () => string;
    /** Rename / toggle visibility / toggle lock of a layer. */
    updateLayer: (id: string, partial: Partial<DiagramLayer>) => void;
    /**
     * Delete a layer and every element/group on it. No-op for the last
     * remaining layer (a document always keeps at least one).
     */
    removeLayer: (id: string) => void;
    /** Move a layer one step toward the back (down) or front (up) in draw order. */
    moveLayer: (id: string, direction: "up" | "down") => void;
    /** Reassign every selected element/group to `layerId`. */
    moveSelectionToLayer: (layerId: string) => void;

    // Groups
    createGroupFromSelection: (nodeIds: string[]) => void;
    /**
     * Group subtool's marquee result. `mode` (set by the modifier held while the
     * rectangle was drawn) selects the operation: `create` makes a new group from
     * the ungrouped nodes hit; `add` merges a single ungrouped node into a single
     * grouped node's group; `remove` drops each node from the last group it joined.
     */
    groupNodesFromMarquee: (nodeIds: string[], mode: GroupToolMode) => void;
    /** Add a single node to a specific group (layer-checked; no-op if invalid). */
    addNodeToGroup: (nodeId: string, groupId: string) => void;
    /** Remove a node from a group (or from every group when `groupId` omitted). */
    removeNodeFromGroup: (nodeId: string, groupId?: string) => void;
    updateGroup: (id: string, partial: Partial<NodeGroup>) => void;
    removeGroup: (id: string) => void;
    /**
     * One-shot request to reveal a group in the control panel's Groups list:
     * the Groups section expands, the group's row scrolls into view, and its
     * editor popover opens. Bumps an internal nonce so re-selecting the same
     * group re-fires. Read by `groupRevealRequest`.
     */
    revealGroup: (groupId: string) => void;
    /**
     * The current reveal request (group id + a monotonically increasing nonce),
     * or null. The Groups section and group rows watch it to expand / scroll /
     * open. The nonce changes on every `revealGroup` call so repeats re-fire.
     */
    groupRevealRequest: { id: string; nonce: number } | null;
    /**
     * Open a config section in the canvas's right tool rail by its rail item id
     * (e.g. "group-outline-presets"). Used by the group popover's jump button.
     */
    revealConfigSection: (sectionId: string) => void;
    /**
     * The current config-section reveal request (rail item id + nonce), or null.
     * The right tool rail watches it to open the matching popover. The nonce
     * changes on every `revealConfigSection` call so repeats re-open.
     */
    configSectionRevealRequest: { id: string; nonce: number } | null;

    // Effects (reusable node decorations, defined once and applied by id)
    addEffect: (kind: EffectKind) => void;
    updateEffect: (id: string, partial: Partial<Effect>) => void;
    removeEffect: (id: string) => void;
    /** Add/remove an effect from a node (toggles membership). */
    toggleNodeEffect: (nodeId: string, effectId: string, on: boolean) => void;

    // Patterns (document-level tiled-SVG fills, applied by id from fills)
    /** Add a tiled-SVG fill pattern wrapping `svg`; returns the new id. */
    addPattern: (name: string, svg: string) => string;
    updatePattern: (id: string, partial: Partial<DiagramPattern>) => void;
    /** Remove a pattern and clear any fills that referenced it (→ no fill). */
    removePattern: (id: string) => void;

    /** Set or clear a host node's spawn pattern (regenerates clones). */
    setNodeSpawn: (id: string, spawn: SpawnPattern | null) => void;
    /** Update spawn pattern params on a host (regenerates clones). */
    updateNodeSpawn: (id: string, partial: Partial<SpawnPattern>) => void;

    // Group properties
    addGroupProperty: (groupId: string, kind: GroupPropertyKind) => void;
    updateGroupProperty: (
        groupId: string,
        index: number,
        partial: Partial<GroupProperty>,
    ) => void;
    removeGroupProperty: (groupId: string, index: number) => void;

    // Group outline presets (reusable outline styles linked by many groups)
    /** Create a new outline preset; returns its id. */
    addGroupOutlinePreset: () => string;
    updateGroupOutlinePreset: (
        id: string,
        partial: Partial<GroupOutlinePreset>,
    ) => void;
    /** Remove a preset; unlinks any group outlines that referenced it. */
    removeGroupOutlinePreset: (id: string) => void;
    /**
     * Link every group's outline to `presetId` (adding an outline to groups that
     * lack one), so a single preset edit restyles all groups at once.
     */
    applyOutlinePresetToAllGroups: (presetId: string) => void;

    // Symmetry
    setSymmetryAxis: (axis: SymmetryAxis, on: boolean) => void;
    /**
     * Toggle an editor-only center line guide. `guideX` draws a vertical line
     * down the canvas's horizontal center; `guideY` a horizontal line across its
     * vertical center.
     */
    setSymmetryGuide: (axis: "guideX" | "guideY", on: boolean) => void;
    /**
     * Snap the current multi-selection into exact symmetry across the active
     * axes: pairs of selected elements are matched by mirror position and the
     * far-side one is moved onto the near-side one's exact reflection. Unpaired
     * elements are left as-is. No-op unless ≥2 elements are selected and at
     * least one axis is active.
     */
    forceSymmetry: () => void;

    // Undo/redo
    undo: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
    pushSnapshot: () => void;

    // Canvas
    viewBoxWidth: number;
    setViewBoxWidth: (v: number) => void;
    viewBoxHeight: number;
    setViewBoxHeight: (v: number) => void;
    background: string;
    setBackground: (v: string) => void;

    // Snapping (drag alignment to other nodes / canvas guides)
    snapEnabled: boolean;
    setSnapEnabled: (v: boolean) => void;
    toggleSnapEnabled: () => void;

    // Reference image (canvas-only tracing aid; never exported or persisted)
    refImage: RefImage | null;
    /** Load a reference image from a picked file (reads it as a data URL). */
    setRefImageFromFile: (file: File) => void;
    /** Drop the reference image. */
    clearRefImage: () => void;
    /** Whether the reference image is currently shown on the canvas. */
    refImageVisible: boolean;
    setRefImageVisible: (v: boolean) => void;
    /** Reference image opacity, 0–1. */
    refImageOpacity: number;
    setRefImageOpacity: (v: number) => void;

    // Output
    canExport: boolean;
    buildExportSvgString: () => string;
    handleDownload: () => void;

    // Persistence
    getPresetValues: () => Record<string, unknown>;
    applyPreset: (values: Record<string, unknown>) => void;
    resetParameters: () => void;

    // Save / load the whole diagram to a `.json` project file on disk.
    saveProjectToFile: () => void;
    loadProjectFromFile: (file: File) => void;
}

const SvgDiagram2DContext = createContext<SvgDiagram2DState | null>(null);

export function useSvgDiagram2D(): SvgDiagram2DState {
    const ctx = useContext(SvgDiagram2DContext);
    if (!ctx) {
        throw new Error(
            "useSvgDiagram2D must be used within SvgDiagram2DProvider",
        );
    }
    return ctx;
}

function defaultPreset(): Record<string, unknown> {
    return {
        doc: createEmptyDocument(),
        viewBoxWidth: DEFAULT_VIEWBOX_W,
        viewBoxHeight: DEFAULT_VIEWBOX_H,
        background: DEFAULT_BACKGROUND,
    };
}

export function SvgDiagram2DProvider({ children }: { children: ReactNode }) {
    const [doc, setDoc] = useState<DiagramDocument>(createEmptyDocument);
    const docRef = useRef(doc);
    docRef.current = doc;

    /**
     * The layer new elements land on. Held in a ref alongside the state so the
     * stable create callbacks (which read `docRef`) can stamp it without taking
     * it as a dependency. `resolveActiveLayerId` falls back to the first layer
     * if the active id ever points at a deleted layer.
     */
    const activeLayerIdRef = useRef("");
    const resolveActiveLayerId = useCallback((): string => {
        const layers = docRef.current.layers;
        const current = activeLayerIdRef.current;
        if (current && layers.some((l) => l.id === current)) return current;
        return layers[0]?.id ?? "";
    }, []);

    /**
     * Drop any ids that live on a locked layer (locked layers can't be selected
     * or edited on the canvas). `pick` reads an element's `layerId` from the
     * current document by id; an unknown id is kept (it's not on any layer).
     */
    const dropLockedIds = useCallback(
        (
            ids: string[],
            pick: (doc: DiagramDocument, id: string) => string | undefined,
        ): string[] => {
            const lockedLayerIds = new Set(
                docRef.current.layers
                    .filter((l) => l.locked)
                    .map((l) => l.id),
            );
            if (lockedLayerIds.size === 0) return ids;
            const d = docRef.current;
            return ids.filter((id) => {
                const layerId = pick(d, id);
                return !layerId || !lockedLayerIds.has(layerId);
            });
        },
        [],
    );
    const nodeLayer = (d: DiagramDocument, id: string) =>
        d.nodes.find((n) => n.id === id)?.layerId;
    const ellipseLayer = (d: DiagramDocument, id: string) =>
        d.ellipses.find((e) => e.id === id)?.layerId;
    const lineLayer = (d: DiagramDocument, id: string) =>
        d.lines.find((l) => l.id === id)?.layerId;
    const arcLayer = (d: DiagramDocument, id: string) =>
        d.arcs.find((a) => a.id === id)?.layerId;
    const rectLayer = (d: DiagramDocument, id: string) =>
        d.rects.find((r) => r.id === id)?.layerId;
    const polygonLayer = (d: DiagramDocument, id: string) =>
        d.polygons.find((p) => p.id === id)?.layerId;
    const heatZoneLayer = (d: DiagramDocument, id: string) =>
        d.heatZones.find((z) => z.id === id)?.layerId;

    const history = useUndoRedo<DiagramDocument>({ clone: cloneDocument });
    const pushSnapshot = useCallback(() => {
        history.push(docRef.current);
    }, [history]);
    const undo = useCallback(() => {
        history.undo(docRef.current, setDoc);
    }, [history]);
    const redo = useCallback(() => {
        history.redo(docRef.current, setDoc);
    }, [history]);

    const [tool, setToolState] = useState<DiagramTool>("select");
    const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
    const [selectedEllipseIds, setSelectedEllipseIds] = useState<string[]>([]);
    const [selectedLineIds, setSelectedLineIds] = useState<string[]>([]);
    const [selectedArcIds, setSelectedArcIds] = useState<string[]>([]);
    const [selectedRectIds, setSelectedRectIds] = useState<string[]>([]);
    const [selectedPolygonIds, setSelectedPolygonIds] = useState<string[]>([]);
    const [selectedHeatZoneIds, setSelectedHeatZoneIds] = useState<string[]>([]);
    const clipboardRef = useRef<ShapeClipboard | null>(null);
    const styleClipboardRef = useRef<NodeStyleClipboard | null>(null);
    const [nodePanelOpen, setNodePanelOpen] = useState(false);
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    // A one-shot request to reveal a group in the control panel's Groups list
    // (expand the section, scroll the row into view, and open its popover).
    // The nonce makes repeated requests for the same group re-fire the effect.
    const [groupToReveal, setGroupToReveal] = useState<{
        id: string;
        nonce: number;
    } | null>(null);
    const revealGroup = useCallback((groupId: string) => {
        setGroupToReveal((prev) => ({
            id: groupId,
            nonce: (prev?.nonce ?? 0) + 1,
        }));
    }, []);
    // A one-shot request to open a config section in the canvas's right tool
    // rail (e.g. jump to "Group outline presets" from a group's popover). Same
    // nonce trick so repeat requests for the same section re-open it.
    const [configSectionToReveal, setConfigSectionToReveal] = useState<{
        id: string;
        nonce: number;
    } | null>(null);
    const revealConfigSection = useCallback((sectionId: string) => {
        setConfigSectionToReveal((prev) => ({
            id: sectionId,
            nonce: (prev?.nonce ?? 0) + 1,
        }));
    }, []);
    const [connectSourceId, setConnectSourceId] = useState<string | null>(null);
    const [activePresetId, setActivePresetId] = useState<string | null>(
        doc.presets[0]?.id ?? null,
    );
    const [activeLayerId, setActiveLayerId] = useState<string>(
        doc.layers[0]?.id ?? "",
    );
    activeLayerIdRef.current = activeLayerId;

    const [viewBoxWidth, setViewBoxWidth] = useState(DEFAULT_VIEWBOX_W);
    const [viewBoxHeight, setViewBoxHeight] = useState(DEFAULT_VIEWBOX_H);
    const [background, setBackground] = useState(DEFAULT_BACKGROUND);
    const viewBoxRef = useRef({ w: viewBoxWidth, h: viewBoxHeight });
    viewBoxRef.current = { w: viewBoxWidth, h: viewBoxHeight };

    // Node-brush composite subtool options (persisted with the rest of the
    // tool params via getPresetValues / applyPreset). A plain object in state;
    // setNodeBrushOptions merges a partial.
    const [nodeBrushOptions, setNodeBrushOptionsState] =
        useState<NodeBrushOptions>(createNodeBrushOptions);
    const setNodeBrushOptions = useCallback(
        (partial: Partial<NodeBrushOptions>) => {
            setNodeBrushOptionsState((prev) => ({ ...prev, ...partial }));
        },
        [],
    );

    // Connect-selection subtool options (persisted alongside the brush options):
    // the wiring pattern and proximity thresholds applied to the current
    // selection by connectSelectedNodes.
    const [connectSelectionOptions, setConnectSelectionOptionsState] =
        useState<ConnectSelectionOptions>(createConnectSelectionOptions);
    const setConnectSelectionOptions = useCallback(
        (partial: Partial<ConnectSelectionOptions>) => {
            setConnectSelectionOptionsState((prev) => ({ ...prev, ...partial }));
        },
        [],
    );

    // Multi-node-brush composite subtool options (persisted alongside the node
    // brush options). The per-stroke appearance/connect/group settings are the
    // shared nodeBrushOptions above; these only describe how the strokes spread.
    const [multiNodeBrushOptions, setMultiNodeBrushOptionsState] =
        useState<MultiNodeBrushOptions>(createMultiNodeBrushOptions);
    const setMultiNodeBrushOptions = useCallback(
        (partial: Partial<MultiNodeBrushOptions>) => {
            setMultiNodeBrushOptionsState((prev) => ({ ...prev, ...partial }));
        },
        [],
    );

    // Mega-node-brush composite subtool options (persisted alongside the node
    // and multi-node brush options). The per-stamp settings are the shared
    // multiNodeBrushOptions / nodeBrushOptions; these only describe how the
    // multi-node stamps spread.
    const [megaNodeBrushOptions, setMegaNodeBrushOptionsState] =
        useState<MegaNodeBrushOptions>(createMegaNodeBrushOptions);
    const setMegaNodeBrushOptions = useCallback(
        (partial: Partial<MegaNodeBrushOptions>) => {
            setMegaNodeBrushOptionsState((prev) => ({ ...prev, ...partial }));
        },
        [],
    );

    // Node-interact subtool options (persisted alongside the brush options): the
    // attract/repel action, influence radius, per-pulse strength, and falloff.
    const [nodeInteractOptions, setNodeInteractOptionsState] =
        useState<NodeInteractOptions>(createNodeInteractOptions);
    const setNodeInteractOptions = useCallback(
        (partial: Partial<NodeInteractOptions>) => {
            setNodeInteractOptionsState((prev) => ({ ...prev, ...partial }));
        },
        [],
    );

    // When on, dragging a node/ellipse snaps to other nodes' X/Y coordinates
    // and the canvas centerlines / edges (see the viewer's snapDelta()).
    const [snapEnabled, setSnapEnabled] = useState(true);
    const toggleSnapEnabled = useCallback(() => setSnapEnabled((v) => !v), []);

    // Reference image: a canvas-only tracing aid drawn beneath the diagram. It
    // lives purely in this tab's state — never written to the document, saved to
    // localStorage, or embedded in the exported SVG.
    const [refImage, setRefImage] = useState<RefImage | null>(null);
    const [refImageVisible, setRefImageVisible] = useState(true);
    const [refImageOpacity, setRefImageOpacity] = useState(0.5);

    const setRefImageFromFile = useCallback((file: File) => {
        if (!file.type.startsWith("image/")) return;
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl =
                typeof reader.result === "string" ? reader.result : "";
            if (!dataUrl) return;
            const img = new Image();
            img.onload = () => {
                setRefImage({
                    dataUrl,
                    naturalWidth: img.naturalWidth,
                    naturalHeight: img.naturalHeight,
                    name: file.name,
                });
                setRefImageVisible(true);
            };
            img.src = dataUrl;
        };
        reader.readAsDataURL(file);
    }, []);

    const clearRefImage = useCallback(() => setRefImage(null), []);

    const setTool = useCallback((next: DiagramTool) => {
        setConnectSourceId(null);
        setToolState(next);
    }, []);

    // --- Node CRUD --------------------------------------------------------

    const viewCenter = useCallback(
        (): Vec2 => ({
            x: viewBoxRef.current.w / 2,
            y: viewBoxRef.current.h / 2,
        }),
        [],
    );

    const addNodeAt = useCallback(
        (rawPos: Vec2) => {
            pushSnapshot();
            const pos = roundVec2(rawPos);
            const center = viewCenter();
            const layerId = resolveActiveLayerId();
            setDoc((prev) => {
                const node = createNode(pos, prev.nodes.length, layerId);
                // Under symmetry, drop an independent node at each mirrored
                // position that isn't already occupied (geometry match — no
                // stored mirror link).
                const extras: DiagramNode[] = [];
                mirrorPositions(pos, prev.symmetry, center).forEach((mp) => {
                    const occupied =
                        findNodeNearPosition(prev.nodes, mp) ||
                        findNodeNearPosition([node, ...extras], mp);
                    if (occupied) return;
                    extras.push(
                        createNode(
                            mp,
                            prev.nodes.length + 1 + extras.length,
                            layerId,
                        ),
                    );
                });
                setSelectedNodeIds([node.id]);
                return { ...prev, nodes: [...prev.nodes, node, ...extras] };
            });
        },
        [pushSnapshot, viewCenter, resolveActiveLayerId],
    );

    /**
     * Apply one node-brush stroke (the first composite subtool). Drops
     * `options.count` nodes scattered within `options.radius` of `center` using
     * a sunflower (Vogel) distribution so they spread evenly rather than
     * clumping, then — per the options — wires them (sequence chain or complete
     * graph) and/or wraps them in a fresh group. Nodes, connections, and the
     * group are all committed in a single setDoc, so the whole stroke is one
     * undo step. The dropped nodes become the selection.
     *
     * Unlike `addNodeAt` this brush is intentionally symmetry-agnostic: a
     * scatter of points has no single mirror identity, and mixing in mirrored
     * copies would double the node count unpredictably. (Symmetry-aware
     * variants can be a later composite subtool.)
     */
    /**
     * Build the elements one node-brush stroke emits, without touching state.
     * Returns the nodes, the connections among them, and (when `options.group`)
     * a wrapping group — using `nodeIndexBase`/`groupIndex` to label new nodes
     * and groups continuing from what the document already has. Pure given its
     * args; shared by the single brush and the multi-brush so both wire and
     * group their clusters identically. `seed` feeds the `random` layout so a
     * given stroke is reproducible.
     */
    const buildBrushStroke = useCallback(
        (
            center: Vec2,
            options: NodeBrushOptions,
            layerId: string,
            nodeIndexBase: number,
            groupIndex: number,
            strokeStyles: StrokeStyle[],
            seed: number,
        ): {
            nodes: DiagramNode[];
            connections: Connection[];
            group: NodeGroup | null;
        } => {
            const count = Math.max(1, Math.floor(options.count));
            const radius = Math.max(0, options.radius);
            const positions = nodeBrushPositions(
                center,
                radius,
                count,
                options.layout,
                seed,
            );
            const nodes: DiagramNode[] = positions.map((p, i) =>
                createNode(roundVec2(p), nodeIndexBase + i, layerId),
            );

            // Connections among the dropped nodes (only when asked, a preset
            // exists, and there are at least two nodes to link).
            const connections: Connection[] = [];
            if (options.connect && activePresetId && nodes.length >= 2) {
                const link = (a: DiagramNode, b: DiagramNode) => {
                    connections.push({
                        id: newId("conn"),
                        layerId,
                        sourceId: a.id,
                        targetId: b.id,
                        presetId: activePresetId,
                    });
                };
                if (options.connectMode === "complete") {
                    for (let i = 0; i < nodes.length; i += 1) {
                        for (let j = i + 1; j < nodes.length; j += 1) {
                            link(nodes[i]!, nodes[j]!);
                        }
                    }
                } else {
                    // Sequence: A→B→C→…→A (a closed chain). With two nodes this
                    // is just the single A→B link (the back-link would duplicate
                    // it, so skip it).
                    for (let i = 0; i < nodes.length; i += 1) {
                        const next = nodes[(i + 1) % nodes.length]!;
                        if (nodes.length === 2 && i === 1) break;
                        link(nodes[i]!, next);
                    }
                }
            }

            const group = options.group
                ? createGroup(
                      nodes.map((n) => n.id),
                      groupIndex,
                      layerId,
                      strokeStyles,
                  )
                : null;

            return { nodes, connections, group };
        },
        [activePresetId],
    );

    /** Seed for the `random` layouts: reproducible per press point. */
    const brushSeed = (center: Vec2): number =>
        (Math.round(center.x) * 73856093) ^ (Math.round(center.y) * 19349663);

    const applyNodeBrush = useCallback(
        (center: Vec2, options: NodeBrushOptions) => {
            pushSnapshot();
            const layerId = resolveActiveLayerId();
            setDoc((prev) => {
                const { nodes, connections, group } = buildBrushStroke(
                    center,
                    options,
                    layerId,
                    prev.nodes.length,
                    prev.groups.length,
                    prev.strokeStyles,
                    brushSeed(center),
                );
                setSelectedNodeIds(nodes.map((n) => n.id));
                return {
                    ...prev,
                    nodes: [...prev.nodes, ...nodes],
                    connections: [...prev.connections, ...connections],
                    groups: group ? [...prev.groups, group] : prev.groups,
                };
            });
        },
        [pushSnapshot, resolveActiveLayerId, buildBrushStroke],
    );

    /**
     * Build the elements one multi-node-brush stamp emits, without touching
     * state — the "brush of brushes". Scatters `multiOptions.count` stroke
     * centers within `multiOptions.spreadRadius` of `center` (arranged per
     * `multiOptions.layout`, the same layout helper a single brush uses for its
     * nodes) and runs `buildBrushStroke` at each; with `groupAll` on, one extra
     * group wraps the union of all stamped nodes. `nodeIndexBase`/`groupIndexBase`
     * continue the document's labelling. Pure given its args; shared by the
     * multi-node brush and the mega-node brush so both spread and group stamps
     * identically.
     */
    const buildMultiBrushStamp = useCallback(
        (
            center: Vec2,
            multiOptions: MultiNodeBrushOptions,
            brushOptions: NodeBrushOptions,
            layerId: string,
            nodeIndexBase: number,
            groupIndexBase: number,
            strokeStyles: StrokeStyle[],
            seed: number,
        ): {
            nodes: DiagramNode[];
            connections: Connection[];
            groups: NodeGroup[];
        } => {
            const strokeCount = Math.max(1, Math.floor(multiOptions.count));
            const spreadRadius = Math.max(0, multiOptions.spreadRadius);
            const centers = nodeBrushPositions(
                center,
                spreadRadius,
                strokeCount,
                multiOptions.layout,
                seed,
            );
            const nodes: DiagramNode[] = [];
            const connections: Connection[] = [];
            const groups: NodeGroup[] = [];
            for (const c of centers) {
                const stroke = buildBrushStroke(
                    c,
                    brushOptions,
                    layerId,
                    nodeIndexBase + nodes.length,
                    groupIndexBase + groups.length,
                    strokeStyles,
                    brushSeed(c),
                );
                nodes.push(...stroke.nodes);
                connections.push(...stroke.connections);
                if (stroke.group) groups.push(stroke.group);
            }

            if (multiOptions.groupAll && nodes.length >= 2) {
                groups.push(
                    createGroup(
                        nodes.map((n) => n.id),
                        groupIndexBase + groups.length,
                        layerId,
                        strokeStyles,
                    ),
                );
            }

            return { nodes, connections, groups };
        },
        [buildBrushStroke],
    );

    /**
     * Apply one multi-node-brush stamp (the "brush of brushes" composite
     * subtool): build a multi-brush stamp at `center` and commit it in a single
     * setDoc / undo step. The dropped nodes become the selection.
     */
    const applyMultiNodeBrush = useCallback(
        (
            center: Vec2,
            multiOptions: MultiNodeBrushOptions,
            brushOptions: NodeBrushOptions,
        ) => {
            pushSnapshot();
            const layerId = resolveActiveLayerId();
            setDoc((prev) => {
                const { nodes, connections, groups } = buildMultiBrushStamp(
                    center,
                    multiOptions,
                    brushOptions,
                    layerId,
                    prev.nodes.length,
                    prev.groups.length,
                    prev.strokeStyles,
                    brushSeed(center),
                );
                setSelectedNodeIds(nodes.map((n) => n.id));
                return {
                    ...prev,
                    nodes: [...prev.nodes, ...nodes],
                    connections: [...prev.connections, ...connections],
                    groups: [...prev.groups, ...groups],
                };
            });
        },
        [pushSnapshot, resolveActiveLayerId, buildMultiBrushStamp],
    );

    /**
     * Apply one mega-node-brush stamp (the "brush of multi-node brushes"
     * composite subtool), distributed per `megaOptions.mode`:
     * - `scatter`: scatter `megaOptions.count` stamp centers within
     *   `megaOptions.spreadRadius` of `center` (arranged per `megaOptions.layout`)
     *   and run the same multi-node brush at each.
     * - `concentric`: stack every stamp on `center`, growing each successive
     *   stamp's multi-node spread radius by `megaOptions.radiusStep` (starting
     *   from `multiOptions.spreadRadius`) so they nest as concentric rings.
     * Each stamp is a full multi-node brush (`multiOptions` + `brushOptions`).
     * Every stamp's nodes, connections, and groups are committed in one setDoc,
     * so the whole thing is a single undo step; with `groupAll` on, one extra
     * group wraps the union of all stamped nodes. The dropped nodes become the
     * selection.
     */
    const applyMegaNodeBrush = useCallback(
        (
            center: Vec2,
            megaOptions: MegaNodeBrushOptions,
            multiOptions: MultiNodeBrushOptions,
            brushOptions: NodeBrushOptions,
        ) => {
            const stampCount = Math.max(1, Math.floor(megaOptions.count));
            const concentric = megaOptions.mode === "concentric";
            pushSnapshot();
            const layerId = resolveActiveLayerId();
            // Scatter: spread the stamp centers per the layout. Concentric: every
            // stamp shares the press point and grows its spread radius instead.
            const centers = concentric
                ? Array.from({ length: stampCount }, () => center)
                : nodeBrushPositions(
                      center,
                      Math.max(0, megaOptions.spreadRadius),
                      stampCount,
                      megaOptions.layout,
                      brushSeed(center),
                  );
            const baseRadius = Math.max(0, multiOptions.spreadRadius);
            const radiusStep = Math.max(0, megaOptions.radiusStep);
            const baseCount = Math.max(1, Math.floor(multiOptions.count));
            const countStep = Math.max(0, Math.floor(megaOptions.countStep));
            setDoc((prev) => {
                const allNodes: DiagramNode[] = [];
                const allConnections: Connection[] = [];
                const newGroups: NodeGroup[] = [];
                centers.forEach((c, i) => {
                    // In concentric mode each ring gets its own grown spread
                    // radius, a stroke count that grows outward (so outer rings
                    // hold more clusters), and a distinct seed (every center is
                    // identical, so a shared seed would scatter every ring alike).
                    const stampMulti = concentric
                        ? {
                              ...multiOptions,
                              spreadRadius: baseRadius + i * radiusStep,
                              count: baseCount + i * countStep,
                          }
                        : multiOptions;
                    const seed = concentric
                        ? brushSeed(center) ^ Math.imul(i + 1, 0x9e3779b1)
                        : brushSeed(c);
                    const stamp = buildMultiBrushStamp(
                        c,
                        stampMulti,
                        brushOptions,
                        layerId,
                        prev.nodes.length + allNodes.length,
                        prev.groups.length + newGroups.length,
                        prev.strokeStyles,
                        seed,
                    );
                    allNodes.push(...stamp.nodes);
                    allConnections.push(...stamp.connections);
                    newGroups.push(...stamp.groups);
                });

                if (megaOptions.groupAll && allNodes.length >= 2) {
                    newGroups.push(
                        createGroup(
                            allNodes.map((n) => n.id),
                            prev.groups.length + newGroups.length,
                            layerId,
                            prev.strokeStyles,
                        ),
                    );
                }

                setSelectedNodeIds(allNodes.map((n) => n.id));
                return {
                    ...prev,
                    nodes: [...prev.nodes, ...allNodes],
                    connections: [...prev.connections, ...allConnections],
                    groups: [...prev.groups, ...newGroups],
                };
            });
        },
        [pushSnapshot, resolveActiveLayerId, buildMultiBrushStamp],
    );

    /**
     * Plan the moves for `id`→`rawPos` plus any symmetric counterparts: under
     * active symmetry, the moved node's mirror counterpart is dragged to the
     * corresponding NEW mirrored position. Returns a flat id→position list;
     * spawn clones and unknown ids contribute nothing. `claimed` records ids
     * already targeted by other movers in the same batch so two movers don't
     * fight over one counterpart.
     *
     * `pinnedMirrorIds` (when given) names the counterpart node for each mirror,
     * in `mirrorPositions` order, locked in by the caller at drag start. With it,
     * the counterparts are driven directly rather than re-found by geometry every
     * frame — which is what a continuous drag MUST do: as the moved node sweeps
     * across another symmetric node, the moved node's mirror image sweeps across
     * that node's counterpart, and a per-frame geometry match would latch onto
     * the wrong (passed-over) counterpart and drop the real one. When omitted
     * (panel edits, paste, align), counterparts are matched by geometry against
     * the moved node's OLD mirrored position, which is fine for one-shot moves.
     */
    const planSymmetricMove = useCallback(
        (
            nodes: DiagramNode[],
            id: string,
            rawPos: Vec2,
            claimed: Set<string>,
            pinnedMirrorIds?: ReadonlyArray<string | null>,
        ): Array<{ id: string; pos: Vec2 }> => {
            const target = nodes.find((n) => n.id === id);
            if (!target || target.spawnedFrom) return [];
            const newPos = roundVec2(rawPos);
            const out: Array<{ id: string; pos: Vec2 }> = [{ id, pos: newPos }];
            claimed.add(id);

            const center = viewCenter();
            const newMirrors = mirrorPositions(
                newPos,
                docRef.current.symmetry,
                center,
            );
            // Pinned counterparts (drag start) are driven directly; otherwise we
            // re-find by geometry at the node's previous-frame mirrored position.
            const oldMirrors = pinnedMirrorIds
                ? null
                : mirrorPositions(
                      target.position,
                      docRef.current.symmetry,
                      center,
                  );
            for (let i = 0; i < newMirrors.length; i += 1) {
                let counterpart: DiagramNode | undefined;
                if (pinnedMirrorIds) {
                    const pinnedId = pinnedMirrorIds[i];
                    if (!pinnedId || claimed.has(pinnedId)) continue;
                    counterpart = nodes.find((n) => n.id === pinnedId);
                } else {
                    if (!oldMirrors || i >= oldMirrors.length) continue;
                    counterpart = findNodeNearPosition(
                        nodes,
                        oldMirrors[i]!,
                        undefined,
                        claimed,
                    );
                }
                if (!counterpart || counterpart.spawnedFrom) continue;
                claimed.add(counterpart.id);
                out.push({ id: counterpart.id, pos: { ...newMirrors[i]! } });
            }
            return out;
        },
        [viewCenter],
    );

    /**
     * The counterpart node ids for `id`'s active-symmetry mirrors, in
     * `mirrorPositions` order, matched by geometry against the CURRENT document.
     * Captured once at drag start and fed back through `moveNodes` as
     * `mirrorIds` so the drag keeps driving the same counterparts even as the
     * moved node sweeps past others (see `planSymmetricMove`). A mirror with no
     * counterpart node yields `null`. `claimed` keeps one node from being grabbed
     * as two different movers' counterpart.
     */
    const findNodeMirrorIds = useCallback(
        (id: string, claimed: Set<string>): (string | null)[] => {
            const nodes = docRef.current.nodes;
            const target = nodes.find((n) => n.id === id);
            if (!target) return [];
            const center = viewCenter();
            const mirrors = mirrorPositions(
                target.position,
                docRef.current.symmetry,
                center,
            );
            return mirrors.map((mp) => {
                const counterpart = findNodeNearPosition(
                    nodes,
                    mp,
                    undefined,
                    claimed,
                );
                if (!counterpart) return null;
                claimed.add(counterpart.id);
                return counterpart.id;
            });
        },
        [viewCenter],
    );

    /** Apply a flat list of id→position moves, regenerating any spawn hosts. */
    const applyNodeMoves = useCallback(
        (
            nodes: DiagramNode[],
            moves: Array<{ id: string; pos: Vec2 }>,
        ): DiagramNode[] => {
            if (moves.length === 0) return nodes;
            const posById = new Map(moves.map((m) => [m.id, m.pos]));
            let next = nodes.map((n) => {
                const p = posById.get(n.id);
                return p ? { ...n, position: { ...p } } : n;
            });
            for (const id of posById.keys()) {
                if (next.find((n) => n.id === id)?.spawn) {
                    next = regenerateNodeSpawn(next, id);
                }
            }
            return next;
        },
        [],
    );

    const moveNode = useCallback(
        (id: string, rawPos: Vec2) => {
            setDoc((prev) => {
                const moves = planSymmetricMove(
                    prev.nodes,
                    id,
                    rawPos,
                    new Set<string>(),
                );
                if (moves.length === 0) return prev;
                return { ...prev, nodes: applyNodeMoves(prev.nodes, moves) };
            });
        },
        [planSymmetricMove, applyNodeMoves],
    );

    /**
     * Apply one node-interact pulse dispatched on `hostId`. The candidate nodes
     * are every other node that isn't a spawn clone (clones are derived from a
     * host pattern) and isn't on a locked layer. For `attract`/`repel`,
     * `nodeInteractionMoves` pulls/pushes those within range; for `connect`,
     * `nodeConnectPulsePairs` chooses the topology and each pair is funneled
     * through `buildConnectionAdditions` (same-layer / no-duplicate / symmetry /
     * active preset). Committed in one setDoc / undo step, and only when there's
     * something to do, so a click that changes nothing costs no history.
     */
    const applyNodeInteraction = useCallback(
        (hostId: string) => {
            const prev = docRef.current;
            const host = prev.nodes.find((n) => n.id === hostId);
            if (!host) return;
            const lockedLayerIds = new Set(
                prev.layers.filter((l) => l.locked).map((l) => l.id),
            );
            const candidates = prev.nodes.filter(
                (n) =>
                    n.id !== hostId &&
                    !n.spawnedFrom &&
                    !lockedLayerIds.has(n.layerId),
            );

            // Connect action: add connections (no movement). Needs an active
            // preset; reuses buildConnectionAdditions so links respect layers,
            // existing connections, and symmetry like every other connect path.
            if (nodeInteractOptions.action === "connect") {
                if (!activePresetId) return;
                const center = viewCenter();
                // Under active symmetry, dispatch the pulse from the host AND
                // from each mirror counterpart node found at the host's mirrored
                // positions — so the opposite node connects to ITS own neighbors
                // in the same fashion (the topology is recomputed there rather
                // than assuming the neighborhood is a perfect mirror). Each host's
                // edges still flow through buildConnectionAdditions, which also
                // mirrors any individual edge whose both endpoints have
                // counterparts. A shared candidate set excludes every dispatch
                // host so no host links to another host as if it were a neighbor.
                //
                // The counterpart is matched within the subtool's configurable
                // symmetry tolerance (default: the tight exact-match value). A
                // layout that's only approximately symmetric — hand-placed or
                // nudged off the exact canvas-center reflection — won't match at
                // the default; the user raises this to bridge the known drift.
                const hostMatchTol = nodeInteractOptions.symmetryTolerance;
                const hosts: DiagramNode[] = [host];
                if (prev.symmetry.x || prev.symmetry.y) {
                    for (const mp of mirrorPositions(
                        host.position,
                        prev.symmetry,
                        center,
                    )) {
                        const mirrorHost = findNodeNearPosition(
                            prev.nodes,
                            mp,
                            hostMatchTol,
                        );
                        if (
                            mirrorHost &&
                            !mirrorHost.spawnedFrom &&
                            !lockedLayerIds.has(mirrorHost.layerId) &&
                            !hosts.some((h) => h.id === mirrorHost.id)
                        ) {
                            hosts.push(mirrorHost);
                        }
                    }
                }
                const hostIds = new Set(hosts.map((h) => h.id));
                const added: Connection[] = [];
                for (const h of hosts) {
                    const pairs = nodeConnectPulsePairs(
                        h,
                        candidates.filter((c) => !hostIds.has(c.id)),
                        nodeInteractOptions,
                    );
                    for (const { a, b } of pairs) {
                        added.push(
                            ...buildConnectionAdditions(
                                prev.nodes,
                                [...prev.connections, ...added],
                                a,
                                b,
                                activePresetId,
                                prev.symmetry,
                                center,
                            ),
                        );
                    }
                }
                if (added.length === 0) return;
                pushSnapshot();
                setDoc((cur) => ({
                    ...cur,
                    connections: [...cur.connections, ...added],
                }));
                return;
            }

            // Attract / repel. Under active symmetry, mirror the whole effect by
            // firing the same pulse from each mirror of the host position as well
            // as from the host itself — so the field is reflected across the
            // active axes regardless of whether the scene has pre-mirrored
            // counterpart nodes (it works on whatever nodes lie under each ring,
            // matching the influence-radius preview, which draws a ring at every
            // mirror position). Each pulse computes its own moves from the full
            // candidate set; per node the LARGEST displacement wins, so a node in
            // range of two rings is pulled/pushed by the stronger one rather than
            // having the pulses cancel or double up.
            const sources = [
                host.position,
                ...(prev.symmetry.x || prev.symmetry.y
                    ? mirrorPositions(host.position, prev.symmetry, viewCenter())
                    : []),
            ];
            const posById = new Map<string, Vec2>();
            const distById = new Map<string, number>();
            const positionById = new Map(
                candidates.map((c) => [c.id, c.position]),
            );
            for (const src of sources) {
                for (const m of nodeInteractionMoves(
                    src,
                    candidates,
                    nodeInteractOptions,
                )) {
                    const from = positionById.get(m.id)!;
                    const d = Math.hypot(m.pos.x - from.x, m.pos.y - from.y);
                    if (d > (distById.get(m.id) ?? -1)) {
                        distById.set(m.id, d);
                        posById.set(m.id, m.pos);
                    }
                }
            }
            if (posById.size === 0) return;

            pushSnapshot();
            setDoc((cur) => ({
                ...cur,
                nodes: applyNodeMoves(
                    cur.nodes,
                    Array.from(posById, ([id, pos]) => ({ id, pos })),
                ),
            }));
        },
        [
            nodeInteractOptions,
            activePresetId,
            viewCenter,
            pushSnapshot,
            applyNodeMoves,
        ],
    );

    /**
     * Move several nodes at once (used to drag a whole selection together).
     * Each update may carry `mirrorIds`: the symmetric counterpart node ids
     * (in `mirrorPositions` order) pinned at drag start, so a continuous drag
     * keeps driving the same counterparts instead of re-finding them by
     * geometry each frame (see `planSymmetricMove`).
     */
    const moveNodes = useCallback(
        (
            updates: Array<{
                id: string;
                pos: Vec2;
                mirrorIds?: ReadonlyArray<string | null>;
            }>,
        ) => {
            if (updates.length === 0) return;
            setDoc((prev) => {
                // Plan each requested move plus its symmetric counterparts. A
                // shared `claimed` set keeps explicit movers and already-paired
                // counterparts from being grabbed twice; explicit updates win
                // over symmetry, so seed them first. Last write per id wins.
                const claimed = new Set<string>(updates.map((u) => u.id));
                const posById = new Map<string, Vec2>();
                for (const u of updates) {
                    for (const m of planSymmetricMove(
                        prev.nodes,
                        u.id,
                        u.pos,
                        claimed,
                        u.mirrorIds,
                    )) {
                        posById.set(m.id, m.pos);
                    }
                }
                if (posById.size === 0) return prev;
                const moves = Array.from(posById, ([id, pos]) => ({ id, pos }));
                return { ...prev, nodes: applyNodeMoves(prev.nodes, moves) };
            });
        },
        [planSymmetricMove, applyNodeMoves],
    );

    const updateNode = useCallback(
        (id: string, partial: Partial<DiagramNode>) => {
            if (partial.position) {
                moveNode(id, partial.position);
                const rest = { ...partial };
                delete rest.position;
                if (Object.keys(rest).length === 0) return;
                partial = rest;
            }
            setDoc((prev) => {
                let nodes = prev.nodes.map((n) =>
                    n.id === id ? { ...n, ...partial } : n,
                );
                const hostIds = new Set<string>();
                if (partial.spawn !== undefined) hostIds.add(id);
                if (SPAWN_TEMPLATE_APPEARANCE_KEYS.some((k) => k in partial)) {
                    for (const hostId of hostIdsUsingTemplate(nodes, id)) {
                        hostIds.add(hostId);
                    }
                }
                for (const hostId of hostIds) {
                    nodes = regenerateNodeSpawn(nodes, hostId);
                }
                return { ...prev, nodes };
            });
        },
        [moveNode],
    );

    const setNodeSpawn = useCallback(
        (id: string, spawn: SpawnPattern | null) => {
            pushSnapshot();
            setDoc((prev) => {
                let nodes = prev.nodes.map((n) =>
                    n.id === id ? { ...n, spawn } : n,
                );
                nodes = regenerateNodeSpawn(nodes, id);
                return { ...prev, nodes };
            });
        },
        [pushSnapshot],
    );

    const updateNodeSpawn = useCallback(
        (id: string, partial: Partial<SpawnPattern>) => {
            pushSnapshot();
            setDoc((prev) => {
                const node = prev.nodes.find((n) => n.id === id);
                if (!node?.spawn) return prev;
                const spawn = { ...node.spawn, ...partial } as SpawnPattern;
                let nodes = prev.nodes.map((n) =>
                    n.id === id ? { ...n, spawn } : n,
                );
                nodes = regenerateNodeSpawn(nodes, id);
                return { ...prev, nodes };
            });
        },
        [pushSnapshot],
    );

    const removeNode = useCallback((id: string) => {
        pushSnapshot();
        setDoc((prev) => {
            const removedIds = collectRemovedNodeIds(prev.nodes, id);
            return finalizeNodeRemoval(prev, removedIds);
        });
        setSelectedNodeIds((cur) => cur.filter((nid) => nid !== id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- Connections ------------------------------------------------------

    const addConnection = useCallback(
        (sourceId: string, targetId: string, presetId: string) => {
            if (sourceId === targetId) return;
            pushSnapshot();
            const center = viewCenter();
            setDoc((prev) => {
                const additions = buildConnectionAdditions(
                    prev.nodes,
                    prev.connections,
                    sourceId,
                    targetId,
                    presetId,
                    prev.symmetry,
                    center,
                );
                if (additions.length === 0) return prev;
                return {
                    ...prev,
                    connections: [...prev.connections, ...additions],
                };
            });
        },
        [pushSnapshot, viewCenter],
    );

    const updateConnection = useCallback(
        (id: string, partial: Partial<Connection>) => {
            setDoc((prev) => ({
                ...prev,
                connections: prev.connections.map((c) =>
                    c.id === id ? { ...c, ...partial } : c,
                ),
            }));
        },
        [],
    );

    const removeConnection = useCallback((id: string) => {
        pushSnapshot();
        setDoc((prev) => ({
            ...prev,
            connections: prev.connections.filter((c) => c.id !== id),
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Split a connection by inserting a fresh node at its midpoint: the link
     * A→B is replaced by A→C and C→B (C is the new node placed halfway between
     * the two endpoints' centers), and the new node becomes the sole selection.
     * Both halves inherit the original connection's preset and arc overrides.
     *
     * Under active symmetry the split also propagates to symmetric counterpart
     * connections: for each mirrored endpoint pair that has existing nodes and
     * a connection between them (matched by geometry, the same way the Connect
     * tool / `addConnection` work), a mirrored node is inserted there too and
     * that counterpart connection is rerouted through it. Counterpart inserts
     * are silent followers — only the directly-clicked node becomes selected.
     */
    const insertNodeOnConnection = useCallback(
        (connectionId: string) => {
            pushSnapshot();
            const center = viewCenter();
            setDoc((prev) => {
                const conn = prev.connections.find((c) => c.id === connectionId);
                if (!conn) return prev;
                const src = prev.nodes.find((n) => n.id === conn.sourceId);
                const tgt = prev.nodes.find((n) => n.id === conn.targetId);
                if (!src || !tgt) return prev;

                // Find the connection linking two nodes (either direction).
                const connBetween = (aId: string, bId: string) =>
                    prev.connections.find(
                        (c) =>
                            (c.sourceId === aId && c.targetId === bId) ||
                            (c.sourceId === bId && c.targetId === aId),
                    );

                const midOf = (a: Vec2, b: Vec2): Vec2 =>
                    roundVec2({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

                const arcOverridesOf = (c: Connection) => ({
                    ...(c.arcFlip !== undefined ? { arcFlip: c.arcFlip } : {}),
                    ...(c.arcSweep !== undefined ? { arcSweep: c.arcSweep } : {}),
                });

                // Build the (node, two replacement links) for splitting one
                // connection `c` whose endpoints sit at nodes `a`/`b`. The new
                // node lands at their midpoint and the original direction is
                // preserved: source→C and C→target.
                const planSplit = (
                    c: Connection,
                    a: DiagramNode,
                    b: DiagramNode,
                    index: number,
                ) => {
                    // The split node and both halves stay on the connection's
                    // layer (its endpoints share it — connections can't span
                    // layers), so the new geometry never leaves the layer.
                    const node = createNode(
                        midOf(a.position, b.position),
                        index,
                        c.layerId,
                    );
                    const overrides = arcOverridesOf(c);
                    const first: Connection = {
                        id: newId("conn"),
                        layerId: c.layerId,
                        sourceId: c.sourceId,
                        targetId: node.id,
                        presetId: c.presetId,
                        ...overrides,
                    };
                    const second: Connection = {
                        id: newId("conn"),
                        layerId: c.layerId,
                        sourceId: node.id,
                        targetId: c.targetId,
                        presetId: c.presetId,
                        ...overrides,
                    };
                    return { node, removeId: c.id, links: [first, second] };
                };

                const newNodes: DiagramNode[] = [];
                const removedConnIds = new Set<string>();
                const addedConns: Connection[] = [];

                // Primary split (the directly-clicked connection).
                const primary = planSplit(conn, src, tgt, prev.nodes.length);
                newNodes.push(primary.node);
                removedConnIds.add(primary.removeId);
                addedConns.push(...primary.links);

                // Symmetric counterparts: pair the source's and target's mirror
                // positions by index so each symmetric copy of A links to the
                // matching symmetric copy of B (mirrors addConnection's pairing).
                const srcMirrors = mirrorPositions(src.position, prev.symmetry, center);
                const tgtMirrors = mirrorPositions(tgt.position, prev.symmetry, center);
                const pairs = Math.min(srcMirrors.length, tgtMirrors.length);
                for (let i = 0; i < pairs; i += 1) {
                    const a = findNodeNearPosition(prev.nodes, srcMirrors[i]!);
                    const b = findNodeNearPosition(prev.nodes, tgtMirrors[i]!);
                    if (!a || !b) continue;
                    const counterpart = connBetween(a.id, b.id);
                    if (!counterpart) continue;
                    if (removedConnIds.has(counterpart.id)) continue;
                    const plan = planSplit(
                        counterpart,
                        a,
                        b,
                        prev.nodes.length + newNodes.length,
                    );
                    newNodes.push(plan.node);
                    removedConnIds.add(plan.removeId);
                    addedConns.push(...plan.links);
                }

                setSelectedNodeIds([primary.node.id]);
                return {
                    ...prev,
                    nodes: [...prev.nodes, ...newNodes],
                    connections: [
                        ...prev.connections.filter(
                            (c) => !removedConnIds.has(c.id),
                        ),
                        ...addedConns,
                    ],
                };
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [viewCenter],
    );

    // --- Ellipses ---------------------------------------------------------

    /** Replace the ellipse selection with one ellipse (clears it with null). */
    const setSelectedEllipseId = useCallback(
        (id: string | null) => {
            const allowed = id && dropLockedIds([id], ellipseLayer).length > 0;
            setSelectedEllipseIds(allowed ? [id!] : []);
            // A single ellipse click replaces the whole selection.
            if (allowed) {
                setSelectedNodeIds([]);
                setSelectedLineIds([]);
                setSelectedArcIds([]);
                setSelectedRectIds([]);
                setSelectedPolygonIds([]);
                setSelectedHeatZoneIds([]);
            }
        },
        [dropLockedIds],
    );

    /** Shift-click: add/remove a single ellipse from the selection. */
    const toggleEllipseSelection = useCallback(
        (id: string) => {
            if (dropLockedIds([id], ellipseLayer).length === 0) return;
            setSelectedEllipseIds((prev) =>
                prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id],
            );
        },
        [dropLockedIds],
    );

    const addEllipse = useCallback(
        (center: Vec2, rx: number, ry: number): string => {
            pushSnapshot();
            const baseIndex = docRef.current.ellipses.length;
            const layerId = resolveActiveLayerId();
            const ellipse = createEllipse(
                roundVec2(center),
                rx,
                ry,
                baseIndex,
                layerId,
            );
            // Under active symmetry, add a mirrored copy at each mirror of the
            // center (same radii/style), so drawing one ellipse fills its
            // symmetric counterparts too.
            const viewBoxCenter = viewCenter();
            const extras = mirrorPointSets(
                [ellipse.center],
                docRef.current.symmetry,
                viewBoxCenter,
            ).map(({ points }, i) =>
                createEllipse(points[0]!, rx, ry, baseIndex + 1 + i, layerId),
            );
            setDoc((prev) => ({
                ...prev,
                ellipses: [...prev.ellipses, ellipse, ...extras],
            }));
            // A freshly drawn ellipse becomes the sole selection.
            setSelectedNodeIds([]);
            setSelectedLineIds([]);
            setSelectedArcIds([]);
            setSelectedRectIds([]);
            setSelectedPolygonIds([]);
            setSelectedEllipseIds([ellipse.id]);
            return ellipse.id;
        },
        [pushSnapshot, viewCenter, resolveActiveLayerId],
    );

    const updateEllipse = useCallback(
        (id: string, partial: Partial<DiagramEllipse>) => {
            setDoc((prev) => ({
                ...prev,
                ellipses: prev.ellipses.map((e) =>
                    e.id === id ? { ...e, ...partial } : e,
                ),
            }));
        },
        [],
    );

    const moveEllipse = useCallback((id: string, center: Vec2) => {
        const next = roundVec2(center);
        setDoc((prev) => ({
            ...prev,
            ellipses: prev.ellipses.map((e) =>
                e.id === id ? { ...e, center: next } : e,
            ),
        }));
    }, []);

    /** Move several ellipses at once (used to drag a whole selection together). */
    const moveEllipses = useCallback(
        (
            updates: Array<{
                id: string;
                center: Vec2;
                rx?: number;
                ry?: number;
            }>,
        ) => {
            if (updates.length === 0) return;
            const byId = new Map(
                updates.map((u) => [
                    u.id,
                    { center: roundVec2(u.center), rx: u.rx, ry: u.ry },
                ]),
            );
            setDoc((prev) => ({
                ...prev,
                ellipses: prev.ellipses.map((e) => {
                    const m = byId.get(e.id);
                    if (!m) return e;
                    return {
                        ...e,
                        center: m.center,
                        rx: m.rx ?? e.rx,
                        ry: m.ry ?? e.ry,
                    };
                }),
            }));
        },
        [],
    );

    const removeEllipse = useCallback((id: string) => {
        pushSnapshot();
        setDoc((prev) => ({
            ...prev,
            ellipses: prev.ellipses.filter((e) => e.id !== id),
        }));
        setSelectedEllipseIds((cur) => cur.filter((eid) => eid !== id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const removeSelectedEllipses = useCallback(() => {
        if (selectedEllipseIds.length === 0) return;
        const removed = new Set(selectedEllipseIds);
        pushSnapshot();
        setDoc((prev) => ({
            ...prev,
            ellipses: prev.ellipses.filter((e) => !removed.has(e.id)),
        }));
        setSelectedEllipseIds([]);
    }, [selectedEllipseIds, pushSnapshot]);

    // --- Lines ------------------------------------------------------------

    /** Replace the line selection with one line (clears it with null). */
    const setSelectedLineId = useCallback(
        (id: string | null) => {
            const allowed = id && dropLockedIds([id], lineLayer).length > 0;
            setSelectedLineIds(allowed ? [id!] : []);
            // A single line click replaces the whole selection (drop other shapes).
            if (allowed) {
                setSelectedNodeIds([]);
                setSelectedEllipseIds([]);
                setSelectedArcIds([]);
                setSelectedRectIds([]);
                setSelectedPolygonIds([]);
                setSelectedHeatZoneIds([]);
            }
        },
        [dropLockedIds],
    );

    /** Shift-click: add/remove a single line from the selection. */
    const toggleLineSelection = useCallback(
        (id: string) => {
            if (dropLockedIds([id], lineLayer).length === 0) return;
            setSelectedLineIds((prev) =>
                prev.includes(id) ? prev.filter((l) => l !== id) : [...prev, id],
            );
        },
        [dropLockedIds],
    );

    const addLine = useCallback(
        (start: Vec2, end: Vec2): string => {
            pushSnapshot();
            const baseIndex = docRef.current.lines.length;
            const layerId = resolveActiveLayerId();
            const line = createLine(
                roundVec2(start),
                roundVec2(end),
                baseIndex,
                layerId,
            );
            // Under active symmetry, mirror both endpoints together to add a
            // mirrored copy of the line per active mirror.
            const viewBoxCenter = viewCenter();
            const extras = mirrorPointSets(
                [line.start, line.end],
                docRef.current.symmetry,
                viewBoxCenter,
            ).map(({ points }, i) =>
                createLine(points[0]!, points[1]!, baseIndex + 1 + i, layerId),
            );
            setDoc((prev) => ({
                ...prev,
                lines: [...prev.lines, line, ...extras],
            }));
            // A freshly drawn line becomes the sole selection.
            setSelectedNodeIds([]);
            setSelectedEllipseIds([]);
            setSelectedArcIds([]);
            setSelectedRectIds([]);
            setSelectedPolygonIds([]);
            setSelectedLineIds([line.id]);
            return line.id;
        },
        [pushSnapshot, viewCenter, resolveActiveLayerId],
    );

    const updateLine = useCallback(
        (id: string, partial: Partial<DiagramLine>) => {
            setDoc((prev) => ({
                ...prev,
                lines: prev.lines.map((l) =>
                    l.id === id ? { ...l, ...partial } : l,
                ),
            }));
        },
        [],
    );

    const moveLine = useCallback((id: string, start: Vec2, end: Vec2) => {
        const s = roundVec2(start);
        const e = roundVec2(end);
        setDoc((prev) => ({
            ...prev,
            lines: prev.lines.map((l) =>
                l.id === id ? { ...l, start: s, end: e } : l,
            ),
        }));
    }, []);

    const moveLines = useCallback(
        (updates: Array<{ id: string; start: Vec2; end: Vec2 }>) => {
            if (updates.length === 0) return;
            const byId = new Map(
                updates.map((u) => [
                    u.id,
                    { start: roundVec2(u.start), end: roundVec2(u.end) },
                ]),
            );
            setDoc((prev) => ({
                ...prev,
                lines: prev.lines.map((l) => {
                    const m = byId.get(l.id);
                    return m ? { ...l, start: m.start, end: m.end } : l;
                }),
            }));
        },
        [],
    );

    const removeLine = useCallback((id: string) => {
        pushSnapshot();
        setDoc((prev) => ({
            ...prev,
            lines: prev.lines.filter((l) => l.id !== id),
        }));
        setSelectedLineIds((cur) => cur.filter((lid) => lid !== id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const removeSelectedLines = useCallback(() => {
        if (selectedLineIds.length === 0) return;
        const removed = new Set(selectedLineIds);
        pushSnapshot();
        setDoc((prev) => ({
            ...prev,
            lines: prev.lines.filter((l) => !removed.has(l.id)),
        }));
        setSelectedLineIds([]);
    }, [selectedLineIds, pushSnapshot]);

    // --- Arcs -------------------------------------------------------------

    /** Replace the arc selection with one arc (clears it with null). */
    const setSelectedArcId = useCallback(
        (id: string | null) => {
            const allowed = id && dropLockedIds([id], arcLayer).length > 0;
            setSelectedArcIds(allowed ? [id!] : []);
            // A single arc click replaces the whole selection (drop other shapes).
            if (allowed) {
                setSelectedNodeIds([]);
                setSelectedEllipseIds([]);
                setSelectedLineIds([]);
                setSelectedRectIds([]);
                setSelectedPolygonIds([]);
                setSelectedHeatZoneIds([]);
            }
        },
        [dropLockedIds],
    );

    /** Shift-click: add/remove a single arc from the selection. */
    const toggleArcSelection = useCallback(
        (id: string) => {
            if (dropLockedIds([id], arcLayer).length === 0) return;
            setSelectedArcIds((prev) =>
                prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
            );
        },
        [dropLockedIds],
    );

    const addArc = useCallback(
        (start: Vec2, end: Vec2): string => {
            pushSnapshot();
            const baseIndex = docRef.current.arcs.length;
            const layerId = resolveActiveLayerId();
            const arc = createArc(
                roundVec2(start),
                roundVec2(end),
                baseIndex,
                layerId,
            );
            // Under active symmetry, mirror both endpoints together. A reflection
            // reverses the chord's handedness, so invert `flip` on odd-axis
            // mirrors to keep each copy a true mirror image of the bulge side.
            const viewBoxCenter = viewCenter();
            const extras = mirrorPointSets(
                [arc.start, arc.end],
                docRef.current.symmetry,
                viewBoxCenter,
            ).map(({ points, flipped }, i) => {
                const copy = createArc(
                    points[0]!,
                    points[1]!,
                    baseIndex + 1 + i,
                    layerId,
                );
                copy.flip = arc.flip !== flipped;
                copy.sweep = arc.sweep;
                return copy;
            });
            setDoc((prev) => ({
                ...prev,
                arcs: [...prev.arcs, arc, ...extras],
            }));
            // A freshly drawn arc becomes the sole selection.
            setSelectedNodeIds([]);
            setSelectedEllipseIds([]);
            setSelectedLineIds([]);
            setSelectedRectIds([]);
            setSelectedPolygonIds([]);
            setSelectedArcIds([arc.id]);
            return arc.id;
        },
        [pushSnapshot, viewCenter, resolveActiveLayerId],
    );

    const updateArc = useCallback(
        (id: string, partial: Partial<DiagramArc>) => {
            setDoc((prev) => ({
                ...prev,
                arcs: prev.arcs.map((a) =>
                    a.id === id ? { ...a, ...partial } : a,
                ),
            }));
        },
        [],
    );

    const moveArc = useCallback((id: string, start: Vec2, end: Vec2) => {
        const s = roundVec2(start);
        const e = roundVec2(end);
        setDoc((prev) => ({
            ...prev,
            arcs: prev.arcs.map((a) =>
                a.id === id ? { ...a, start: s, end: e } : a,
            ),
        }));
    }, []);

    const moveArcs = useCallback(
        (updates: Array<{ id: string; start: Vec2; end: Vec2 }>) => {
            if (updates.length === 0) return;
            const byId = new Map(
                updates.map((u) => [
                    u.id,
                    { start: roundVec2(u.start), end: roundVec2(u.end) },
                ]),
            );
            setDoc((prev) => ({
                ...prev,
                arcs: prev.arcs.map((a) => {
                    const m = byId.get(a.id);
                    return m ? { ...a, start: m.start, end: m.end } : a;
                }),
            }));
        },
        [],
    );

    const removeArc = useCallback((id: string) => {
        pushSnapshot();
        setDoc((prev) => ({
            ...prev,
            arcs: prev.arcs.filter((a) => a.id !== id),
        }));
        setSelectedArcIds((cur) => cur.filter((aid) => aid !== id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const removeSelectedArcs = useCallback(() => {
        if (selectedArcIds.length === 0) return;
        const removed = new Set(selectedArcIds);
        pushSnapshot();
        setDoc((prev) => ({
            ...prev,
            arcs: prev.arcs.filter((a) => !removed.has(a.id)),
        }));
        setSelectedArcIds([]);
    }, [selectedArcIds, pushSnapshot]);

    // --- Rectangles -------------------------------------------------------

    /** Replace the rect selection with one rect (clears it with null). */
    const setSelectedRectId = useCallback(
        (id: string | null) => {
            const allowed = id && dropLockedIds([id], rectLayer).length > 0;
            setSelectedRectIds(allowed ? [id!] : []);
            // A single rect click replaces the whole selection (drop other shapes).
            if (allowed) {
                setSelectedNodeIds([]);
                setSelectedEllipseIds([]);
                setSelectedLineIds([]);
                setSelectedArcIds([]);
                setSelectedPolygonIds([]);
                setSelectedHeatZoneIds([]);
            }
        },
        [dropLockedIds],
    );

    /** Shift-click: add/remove a single rect from the selection. */
    const toggleRectSelection = useCallback(
        (id: string) => {
            if (dropLockedIds([id], rectLayer).length === 0) return;
            setSelectedRectIds((prev) =>
                prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
            );
        },
        [dropLockedIds],
    );

    const addRect = useCallback(
        (start: Vec2, end: Vec2): string => {
            pushSnapshot();
            const baseIndex = docRef.current.rects.length;
            const layerId = resolveActiveLayerId();
            const rect = createRect(
                roundVec2(start),
                roundVec2(end),
                baseIndex,
                layerId,
            );
            // Under active symmetry, mirror the two corners together to add a
            // mirrored copy of the box per active mirror.
            const viewBoxCenter = viewCenter();
            const extras = mirrorPointSets(
                [rect.start, rect.end],
                docRef.current.symmetry,
                viewBoxCenter,
            ).map(({ points }, i) =>
                createRect(points[0]!, points[1]!, baseIndex + 1 + i, layerId),
            );
            setDoc((prev) => ({
                ...prev,
                rects: [...prev.rects, rect, ...extras],
            }));
            // A freshly drawn rect becomes the sole selection.
            setSelectedNodeIds([]);
            setSelectedEllipseIds([]);
            setSelectedLineIds([]);
            setSelectedArcIds([]);
            setSelectedPolygonIds([]);
            setSelectedRectIds([rect.id]);
            return rect.id;
        },
        [pushSnapshot, viewCenter, resolveActiveLayerId],
    );

    const updateRect = useCallback(
        (id: string, partial: Partial<DiagramRect>) => {
            setDoc((prev) => ({
                ...prev,
                rects: prev.rects.map((r) =>
                    r.id === id ? { ...r, ...partial } : r,
                ),
            }));
        },
        [],
    );

    const moveRect = useCallback((id: string, start: Vec2, end: Vec2) => {
        const s = roundVec2(start);
        const e = roundVec2(end);
        setDoc((prev) => ({
            ...prev,
            rects: prev.rects.map((r) =>
                r.id === id ? { ...r, start: s, end: e } : r,
            ),
        }));
    }, []);

    const moveRects = useCallback(
        (updates: Array<{ id: string; start: Vec2; end: Vec2 }>) => {
            if (updates.length === 0) return;
            const byId = new Map(
                updates.map((u) => [
                    u.id,
                    { start: roundVec2(u.start), end: roundVec2(u.end) },
                ]),
            );
            setDoc((prev) => ({
                ...prev,
                rects: prev.rects.map((r) => {
                    const m = byId.get(r.id);
                    return m ? { ...r, start: m.start, end: m.end } : r;
                }),
            }));
        },
        [],
    );

    const removeRect = useCallback((id: string) => {
        pushSnapshot();
        setDoc((prev) => ({
            ...prev,
            rects: prev.rects.filter((r) => r.id !== id),
        }));
        setSelectedRectIds((cur) => cur.filter((rid) => rid !== id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const removeSelectedRects = useCallback(() => {
        if (selectedRectIds.length === 0) return;
        const removed = new Set(selectedRectIds);
        pushSnapshot();
        setDoc((prev) => ({
            ...prev,
            rects: prev.rects.filter((r) => !removed.has(r.id)),
        }));
        setSelectedRectIds([]);
    }, [selectedRectIds, pushSnapshot]);

    // --- Heat zones -------------------------------------------------------
    // Editor-only rectangular regions; mirror the rect CRUD. They draw nothing
    // on export (only recolor heat-reactive elements), but are full document
    // citizens: selectable, movable, layer-bound, undo-tracked, persisted.

    /** Replace the heat-zone selection with one zone (clears it with null). */
    const setSelectedHeatZoneId = useCallback(
        (id: string | null) => {
            const allowed =
                id && dropLockedIds([id], heatZoneLayer).length > 0;
            setSelectedHeatZoneIds(allowed ? [id!] : []);
            if (allowed) {
                setSelectedNodeIds([]);
                setSelectedEllipseIds([]);
                setSelectedLineIds([]);
                setSelectedArcIds([]);
                setSelectedRectIds([]);
                setSelectedPolygonIds([]);
            }
        },
        [dropLockedIds],
    );

    /** Shift-click: add/remove a single heat zone from the selection. */
    const toggleHeatZoneSelection = useCallback(
        (id: string) => {
            if (dropLockedIds([id], heatZoneLayer).length === 0) return;
            setSelectedHeatZoneIds((prev) =>
                prev.includes(id) ? prev.filter((z) => z !== id) : [...prev, id],
            );
        },
        [dropLockedIds],
    );

    const addHeatZone = useCallback(
        (start: Vec2, end: Vec2): string => {
            pushSnapshot();
            const baseIndex = docRef.current.heatZones.length;
            const layerId = resolveActiveLayerId();
            const zone = createHeatZone(
                roundVec2(start),
                roundVec2(end),
                baseIndex,
                layerId,
            );
            // Under active symmetry, mirror the two corners together to add a
            // mirrored copy of the zone per active mirror.
            const viewBoxCenter = viewCenter();
            const extras = mirrorPointSets(
                [zone.start, zone.end],
                docRef.current.symmetry,
                viewBoxCenter,
            ).map(({ points }, i) =>
                createHeatZone(
                    points[0]!,
                    points[1]!,
                    baseIndex + 1 + i,
                    layerId,
                ),
            );
            setDoc((prev) => ({
                ...prev,
                heatZones: [...prev.heatZones, zone, ...extras],
            }));
            setSelectedNodeIds([]);
            setSelectedEllipseIds([]);
            setSelectedLineIds([]);
            setSelectedArcIds([]);
            setSelectedRectIds([]);
            setSelectedPolygonIds([]);
            setSelectedHeatZoneIds([zone.id]);
            return zone.id;
        },
        [pushSnapshot, viewCenter, resolveActiveLayerId],
    );

    const updateHeatZone = useCallback(
        (id: string, partial: Partial<DiagramHeatZone>) => {
            setDoc((prev) => ({
                ...prev,
                heatZones: prev.heatZones.map((z) =>
                    z.id === id ? { ...z, ...partial } : z,
                ),
            }));
        },
        [],
    );

    const moveHeatZone = useCallback((id: string, start: Vec2, end: Vec2) => {
        const s = roundVec2(start);
        const e = roundVec2(end);
        setDoc((prev) => ({
            ...prev,
            heatZones: prev.heatZones.map((z) =>
                z.id === id ? { ...z, start: s, end: e } : z,
            ),
        }));
    }, []);

    const moveHeatZones = useCallback(
        (updates: Array<{ id: string; start: Vec2; end: Vec2 }>) => {
            if (updates.length === 0) return;
            const byId = new Map(
                updates.map((u) => [
                    u.id,
                    { start: roundVec2(u.start), end: roundVec2(u.end) },
                ]),
            );
            setDoc((prev) => ({
                ...prev,
                heatZones: prev.heatZones.map((z) => {
                    const m = byId.get(z.id);
                    return m ? { ...z, start: m.start, end: m.end } : z;
                }),
            }));
        },
        [],
    );

    const removeHeatZone = useCallback((id: string) => {
        pushSnapshot();
        setDoc((prev) => ({
            ...prev,
            heatZones: prev.heatZones.filter((z) => z.id !== id),
        }));
        setSelectedHeatZoneIds((cur) => cur.filter((zid) => zid !== id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const removeSelectedHeatZones = useCallback(() => {
        if (selectedHeatZoneIds.length === 0) return;
        const removed = new Set(selectedHeatZoneIds);
        pushSnapshot();
        setDoc((prev) => ({
            ...prev,
            heatZones: prev.heatZones.filter((z) => !removed.has(z.id)),
        }));
        setSelectedHeatZoneIds([]);
    }, [selectedHeatZoneIds, pushSnapshot]);

    // --- Polygons ---------------------------------------------------------

    /** Replace the polygon selection with one polygon (clears it with null). */
    const setSelectedPolygonId = useCallback(
        (id: string | null) => {
            const allowed = id && dropLockedIds([id], polygonLayer).length > 0;
            setSelectedPolygonIds(allowed ? [id!] : []);
            // A single polygon click replaces the whole selection (drop others).
            if (allowed) {
                setSelectedNodeIds([]);
                setSelectedEllipseIds([]);
                setSelectedLineIds([]);
                setSelectedArcIds([]);
                setSelectedRectIds([]);
                setSelectedHeatZoneIds([]);
            }
        },
        [dropLockedIds],
    );

    /** Shift-click: add/remove a single polygon from the selection. */
    const togglePolygonSelection = useCallback(
        (id: string) => {
            if (dropLockedIds([id], polygonLayer).length === 0) return;
            setSelectedPolygonIds((prev) =>
                prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
            );
        },
        [dropLockedIds],
    );

    const addPolygon = useCallback(
        (points: Vec2[]): string => {
            pushSnapshot();
            const baseIndex = docRef.current.polygons.length;
            const layerId = resolveActiveLayerId();
            const polygon = createPolygon(
                points.map((p) => roundVec2(p)),
                baseIndex,
                layerId,
            );
            // Under active symmetry, mirror every vertex together to add a
            // mirrored copy of the polygon per active mirror.
            const viewBoxCenter = viewCenter();
            const extras = mirrorPointSets(
                polygon.points,
                docRef.current.symmetry,
                viewBoxCenter,
            ).map(({ points: pts }, i) =>
                createPolygon(pts, baseIndex + 1 + i, layerId),
            );
            setDoc((prev) => ({
                ...prev,
                polygons: [...prev.polygons, polygon, ...extras],
            }));
            // A freshly drawn polygon becomes the sole selection.
            setSelectedNodeIds([]);
            setSelectedEllipseIds([]);
            setSelectedLineIds([]);
            setSelectedArcIds([]);
            setSelectedRectIds([]);
            setSelectedPolygonIds([polygon.id]);
            return polygon.id;
        },
        [pushSnapshot, viewCenter, resolveActiveLayerId],
    );

    const updatePolygon = useCallback(
        (id: string, partial: Partial<DiagramPolygon>) => {
            setDoc((prev) => ({
                ...prev,
                polygons: prev.polygons.map((p) =>
                    p.id === id ? { ...p, ...partial } : p,
                ),
            }));
        },
        [],
    );

    const movePolygon = useCallback((id: string, points: Vec2[]) => {
        const pts = points.map((p) => roundVec2(p));
        setDoc((prev) => ({
            ...prev,
            polygons: prev.polygons.map((p) =>
                p.id === id ? { ...p, points: pts } : p,
            ),
        }));
    }, []);

    const movePolygons = useCallback(
        (updates: Array<{ id: string; points: Vec2[] }>) => {
            if (updates.length === 0) return;
            const byId = new Map(
                updates.map((u) => [
                    u.id,
                    u.points.map((p) => roundVec2(p)),
                ]),
            );
            setDoc((prev) => ({
                ...prev,
                polygons: prev.polygons.map((p) => {
                    const pts = byId.get(p.id);
                    return pts ? { ...p, points: pts } : p;
                }),
            }));
        },
        [],
    );

    const removePolygon = useCallback((id: string) => {
        pushSnapshot();
        setDoc((prev) => ({
            ...prev,
            polygons: prev.polygons.filter((p) => p.id !== id),
        }));
        setSelectedPolygonIds((cur) => cur.filter((pid) => pid !== id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const removeSelectedPolygons = useCallback(() => {
        if (selectedPolygonIds.length === 0) return;
        const removed = new Set(selectedPolygonIds);
        pushSnapshot();
        setDoc((prev) => ({
            ...prev,
            polygons: prev.polygons.filter((p) => !removed.has(p.id)),
        }));
        setSelectedPolygonIds([]);
    }, [selectedPolygonIds, pushSnapshot]);

    // --- Bulk style edit across a mixed multi-selection -------------------

    /**
     * Apply a style patch to every selected standalone shape (ellipse / line /
     * arc / rect / polygon) at once, in a single undo step. Lines and arcs have
     * no fill, so a `fill` key is skipped for them; every other key applies to
     * any shape that has it. Nodes are intentionally excluded — their `color` is
     * a marker fill, not a stroke, so they're edited from their own panel.
     */
    const updateSelectedShapesStyle = useCallback(
        (patch: ShapeStylePatch) => {
            const ell = new Set(selectedEllipseIds);
            const lin = new Set(selectedLineIds);
            const arc = new Set(selectedArcIds);
            const rct = new Set(selectedRectIds);
            const pol = new Set(selectedPolygonIds);
            if (
                ell.size + lin.size + arc.size + rct.size + pol.size === 0
            ) {
                return;
            }
            // Line/arc have no fill; everything else (stroke color/width/dash,
            // stroke style, visibility) is common to all five shape types.
            const { fill: _fill, ...strokeOnly } = patch;
            pushSnapshot();
            setDoc((prev) => ({
                ...prev,
                ellipses: prev.ellipses.map((e) =>
                    ell.has(e.id) ? { ...e, ...patch } : e,
                ),
                lines: prev.lines.map((l) =>
                    lin.has(l.id) ? { ...l, ...strokeOnly } : l,
                ),
                arcs: prev.arcs.map((a) =>
                    arc.has(a.id) ? { ...a, ...strokeOnly } : a,
                ),
                rects: prev.rects.map((r) =>
                    rct.has(r.id) ? { ...r, ...patch } : r,
                ),
                polygons: prev.polygons.map((p) =>
                    pol.has(p.id) ? { ...p, ...patch } : p,
                ),
            }));
        },
        [
            selectedEllipseIds,
            selectedLineIds,
            selectedArcIds,
            selectedRectIds,
            selectedPolygonIds,
            pushSnapshot,
        ],
    );

    // --- connect subtool two-click flow -----------------------------------

    /** Two-click connect flow; only invoked while the connect subtool is active. */
    const handleNodePick = useCallback(
        (id: string) => {
            if (connectSourceId == null) {
                setConnectSourceId(id);
                return;
            }
            if (connectSourceId !== id && activePresetId) {
                addConnection(connectSourceId, id, activePresetId);
            }
            setConnectSourceId(null);
        },
        [connectSourceId, activePresetId, addConnection],
    );

    // --- Selection ----------------------------------------------------------

    const setSelectedNodeId = useCallback(
        (id: string | null) => {
            // Locked layers can't be selected on the canvas.
            const allowed = id && dropLockedIds([id], nodeLayer).length > 0;
            setSelectedNodeIds(allowed ? [id!] : []);
            // A single node click replaces the whole selection (drop other shapes).
            if (allowed) {
                setSelectedEllipseIds([]);
                setSelectedLineIds([]);
                setSelectedArcIds([]);
                setSelectedRectIds([]);
                setSelectedPolygonIds([]);
                setSelectedHeatZoneIds([]);
            }
        },
        [dropLockedIds],
    );

    const toggleNodeSelection = useCallback(
        (id: string) => {
            if (dropLockedIds([id], nodeLayer).length === 0) return;
            setSelectedNodeIds((prev) =>
                prev.includes(id) ? prev.filter((n) => n !== id) : [...prev, id],
            );
        },
        [dropLockedIds],
    );

    const selectNodesInRect = useCallback(
        (ids: string[], additive: boolean) => {
            const allowed = dropLockedIds(ids, nodeLayer);
            setSelectedNodeIds((prev) => {
                if (!additive) return allowed;
                return [...new Set([...prev, ...allowed])];
            });
        },
        [dropLockedIds],
    );

    /**
     * Rectangle-select result spanning every shape type: nodes, ellipses,
     * lines, and arcs are selected together so a marquee can grab a mixed set.
     * `additive` merges into the existing selection (shift-drag); otherwise it
     * replaces it.
     */
    const selectInRect = useCallback(
        (
            nodeIds: string[],
            ellipseIds: string[],
            lineIds: string[],
            arcIds: string[],
            rectIds: string[],
            polygonIds: string[],
            additive: boolean,
        ) => {
            // Drop everything on locked layers from the marquee result.
            const nIds = dropLockedIds(nodeIds, nodeLayer);
            const eIds = dropLockedIds(ellipseIds, ellipseLayer);
            const lIds = dropLockedIds(lineIds, lineLayer);
            const aIds = dropLockedIds(arcIds, arcLayer);
            const rIds = dropLockedIds(rectIds, rectLayer);
            const pIds = dropLockedIds(polygonIds, polygonLayer);
            setSelectedNodeIds((prev) =>
                additive ? [...new Set([...prev, ...nIds])] : nIds,
            );
            setSelectedEllipseIds((prev) =>
                additive ? [...new Set([...prev, ...eIds])] : eIds,
            );
            setSelectedLineIds((prev) =>
                additive ? [...new Set([...prev, ...lIds])] : lIds,
            );
            setSelectedArcIds((prev) =>
                additive ? [...new Set([...prev, ...aIds])] : aIds,
            );
            setSelectedRectIds((prev) =>
                additive ? [...new Set([...prev, ...rIds])] : rIds,
            );
            setSelectedPolygonIds((prev) =>
                additive ? [...new Set([...prev, ...pIds])] : pIds,
            );
        },
        [dropLockedIds],
    );

    const clearSelection = useCallback(() => {
        setSelectedNodeIds([]);
        setSelectedEllipseIds([]);
        setSelectedLineIds([]);
        setSelectedArcIds([]);
        setSelectedRectIds([]);
        setSelectedPolygonIds([]);
        setSelectedHeatZoneIds([]);
    }, []);

    /**
     * Connect the selected nodes following `connectSelectionOptions`: the chosen
     * wiring pattern (complete / sequence / nearest), filtered by the optional
     * min/max proximity thresholds. The pair set is computed by the pure
     * `connectionPairsForSelection`; each pair is then funneled through the
     * symmetry-aware builder so links also connect symmetric counterparts
     * (matching the Connect tool).
     */
    const connectSelectedNodes = useCallback(() => {
        if (selectedNodeIds.length < 2 || !activePresetId) return;
        pushSnapshot();
        const center = viewCenter();
        setDoc((prev) => {
            const pairs = connectionPairsForSelection(
                prev.nodes,
                selectedNodeIds,
                connectSelectionOptions,
            );
            let connections = prev.connections;
            for (const [aId, bId] of pairs) {
                const additions = buildConnectionAdditions(
                    prev.nodes,
                    connections,
                    aId,
                    bId,
                    activePresetId,
                    prev.symmetry,
                    center,
                );
                if (additions.length > 0) {
                    connections = [...connections, ...additions];
                }
            }
            if (connections === prev.connections) return prev;
            return { ...prev, connections };
        });
    }, [
        selectedNodeIds,
        activePresetId,
        connectSelectionOptions,
        pushSnapshot,
        viewCenter,
    ]);

    /** Remove every connection whose both endpoints are selected. */
    const clearConnectionsBetweenSelected = useCallback(() => {
        if (selectedNodeIds.length < 2) return;
        const selected = new Set(selectedNodeIds);
        pushSnapshot();
        setDoc((prev) => {
            const removedIds = new Set<string>();
            for (const c of prev.connections) {
                if (selected.has(c.sourceId) && selected.has(c.targetId)) {
                    removedIds.add(c.id);
                }
            }
            if (removedIds.size === 0) return prev;
            return {
                ...prev,
                connections: prev.connections.filter(
                    (c) => !removedIds.has(c.id),
                ),
            };
        });
    }, [selectedNodeIds, pushSnapshot]);

    // --- Copy / paste / duplicate ----------------------------------------

    const copySelectedNodes = useCallback(() => {
        const d = docRef.current;
        const targets = d.nodes.filter(
            (n) => selectedNodeIds.includes(n.id) && !n.spawnedFrom,
        );
        const selectedEllipses = d.ellipses.filter((el) =>
            selectedEllipseIds.includes(el.id),
        );
        const selectedLines = d.lines.filter((l) =>
            selectedLineIds.includes(l.id),
        );
        const selectedArcs = d.arcs.filter((a) => selectedArcIds.includes(a.id));
        const selectedRects = d.rects.filter((r) =>
            selectedRectIds.includes(r.id),
        );
        const selectedPolygons = d.polygons.filter((p) =>
            selectedPolygonIds.includes(p.id),
        );
        if (
            targets.length === 0 &&
            selectedEllipses.length === 0 &&
            selectedLines.length === 0 &&
            selectedArcs.length === 0 &&
            selectedRects.length === 0 &&
            selectedPolygons.length === 0
        ) {
            clipboardRef.current = null;
            return;
        }
        const indexById = new Map(targets.map((n, i) => [n.id, i]));
        const nodes = targets.map((n) => {
            const { id: _id, spawnedFrom: _spawnedFrom, ...rest } = n;
            void _id;
            void _spawnedFrom;
            const cloned = structuredClone(rest);
            cloned.spawn = null;
            return cloned;
        });
        const connections = d.connections
            .filter(
                (c) => indexById.has(c.sourceId) && indexById.has(c.targetId),
            )
            .map((c) => ({
                sourceIndex: indexById.get(c.sourceId)!,
                targetIndex: indexById.get(c.targetId)!,
                presetId: c.presetId,
            }));
        // Standalone shapes carry no inter-shape references, so copy them
        // verbatim (minus their id, re-minted on paste).
        const stripId = <T extends { id: string }>({
            id: _id,
            ...rest
        }: T): Omit<T, "id"> => {
            void _id;
            return structuredClone(rest);
        };
        clipboardRef.current = {
            nodes,
            connections,
            ellipses: selectedEllipses.map(stripId),
            lines: selectedLines.map(stripId),
            arcs: selectedArcs.map(stripId),
            rects: selectedRects.map(stripId),
            polygons: selectedPolygons.map(stripId),
        };
    }, [
        selectedNodeIds,
        selectedEllipseIds,
        selectedLineIds,
        selectedArcIds,
        selectedRectIds,
        selectedPolygonIds,
    ]);

    const pasteNodes = useCallback(() => {
        const clip = clipboardRef.current;
        if (
            !clip ||
            (clip.nodes.length === 0 &&
                clip.ellipses.length === 0 &&
                clip.lines.length === 0 &&
                clip.arcs.length === 0 &&
                clip.rects.length === 0 &&
                clip.polygons.length === 0)
        ) {
            return;
        }
        pushSnapshot();
        const presets = docRef.current.presets;
        const fallbackPreset = presets[0]?.id ?? null;
        const resolvePreset = (id: string | null): string | null =>
            id && presets.some((p) => p.id === id) ? id : fallbackPreset;

        // A copied element keeps its source layer, unless that layer was deleted
        // between copy and paste — then it falls onto the active layer.
        const liveLayerIds = new Set(docRef.current.layers.map((l) => l.id));
        const activeLayer = resolveActiveLayerId();
        const resolveLayer = (id: string): string =>
            liveLayerIds.has(id) ? id : activeLayer;

        const newIds: string[] = [];
        const newNodes: DiagramNode[] = clip.nodes.map((n) => {
            // effectIds reference document-level effects that still exist, so
            // they carry over unchanged.
            const cloned = structuredClone(n);
            const id = newId("node");
            newIds.push(id);
            return {
                ...cloned,
                id,
                layerId: resolveLayer(cloned.layerId),
                spawnedFrom: null,
                spawn: null,
                position: roundVec2({
                    x: cloned.position.x + PASTE_OFFSET,
                    y: cloned.position.y + PASTE_OFFSET,
                }),
            };
        });
        const pastedConnections: Connection[] = clip.connections.map((c) => ({
            id: newId("conn"),
            // A connection rides its endpoints' shared layer; copied endpoints
            // keep their source layer, so read it back off the pasted source.
            layerId: newNodes[c.sourceIndex]!.layerId,
            sourceId: newIds[c.sourceIndex]!,
            targetId: newIds[c.targetIndex]!,
            presetId: resolvePreset(c.presetId) ?? c.presetId,
        }));
        // Standalone shapes paste with a fresh id and the same offset as nodes;
        // labels are bumped inside the updater so they dedupe against the live
        // document. Endpoints/centers shift by PASTE_OFFSET like node positions.
        const offsetVec = (v: Vec2): Vec2 =>
            roundVec2({ x: v.x + PASTE_OFFSET, y: v.y + PASTE_OFFSET });
        const newEllipseIds: string[] = [];
        const newEllipses: DiagramEllipse[] = clip.ellipses.map((el) => {
            const id = newId("ellipse");
            newEllipseIds.push(id);
            return {
                ...structuredClone(el),
                id,
                layerId: resolveLayer(el.layerId),
                center: offsetVec(el.center),
            };
        });
        const newLineIds: string[] = [];
        const newLines: DiagramLine[] = clip.lines.map((l) => {
            const id = newId("line");
            newLineIds.push(id);
            return {
                ...structuredClone(l),
                id,
                layerId: resolveLayer(l.layerId),
                start: offsetVec(l.start),
                end: offsetVec(l.end),
            };
        });
        const newArcIds: string[] = [];
        const newArcs: DiagramArc[] = clip.arcs.map((a) => {
            const id = newId("arc");
            newArcIds.push(id);
            return {
                ...structuredClone(a),
                id,
                layerId: resolveLayer(a.layerId),
                start: offsetVec(a.start),
                end: offsetVec(a.end),
            };
        });
        const newRectIds: string[] = [];
        const newRects: DiagramRect[] = clip.rects.map((r) => {
            const id = newId("rect");
            newRectIds.push(id);
            return {
                ...structuredClone(r),
                id,
                layerId: resolveLayer(r.layerId),
                start: offsetVec(r.start),
                end: offsetVec(r.end),
            };
        });
        const newPolygonIds: string[] = [];
        const newPolygons: DiagramPolygon[] = clip.polygons.map((p) => {
            const id = newId("polygon");
            newPolygonIds.push(id);
            return {
                ...structuredClone(p),
                id,
                layerId: resolveLayer(p.layerId),
                points: p.points.map(offsetVec),
            };
        });
        setDoc((prev) => {
            const center = {
                x: viewBoxRef.current.w / 2,
                y: viewBoxRef.current.h / 2,
            };
            // Bump trailing numbers on pasted labels ("Node 1" → next free
            // "Node N"), against the live document plus labels handed out within
            // this paste, so the copies don't collide with existing nodes or
            // each other.
            // Don't mutate newNodes (this updater may run twice under
            // StrictMode); relabel into a fresh array instead.
            const usedLabels = new Set(prev.nodes.map((n) => n.label));
            const takeCopyLabel = (label: string): string => {
                const next = nextCopyLabel(label, usedLabels);
                usedLabels.add(next);
                return next;
            };
            const labeledNodes = newNodes.map((n) => ({
                ...n,
                label: takeCopyLabel(n.label),
            }));
            // Under symmetry, drop an independent copy at each pasted node's
            // mirrored positions (skipping occupied spots). symMirrorsByIndex[i]
            // maps each mirror position of labeledNodes[i] to the node now there
            // (a freshly created one, or a pre-existing occupant) so internal
            // connections can be mirrored between matching counterparts.
            const created: DiagramNode[] = [];
            const symMirrorsByIndex: Array<Array<DiagramNode | undefined>> =
                labeledNodes.map((n) => {
                    return mirrorPositions(
                        n.position,
                        prev.symmetry,
                        center,
                    ).map((mp) => {
                        const existing =
                            findNodeNearPosition(prev.nodes, mp) ||
                            findNodeNearPosition(
                                [...labeledNodes, ...created],
                                mp,
                            );
                        if (existing) return existing;
                        const m: DiagramNode = {
                            ...structuredClone({
                                label: takeCopyLabel(n.label),
                                layerId: n.layerId,
                                radius: n.radius,
                                color: n.color,
                                shape: n.shape,
                                visible: n.visible,
                                effectIds: [...n.effectIds],
                            }),
                            id: newId("node"),
                            position: { ...mp },
                            spawn: null,
                            spawnedFrom: null,
                        };
                        created.push(m);
                        return m;
                    });
                });
            // For each pasted connection, add a link between the corresponding
            // counterparts at each mirror index (when both ends have one).
            const mirrorConnections: Connection[] = [];
            for (const conn of pastedConnections) {
                const srcIdx = newIds.indexOf(conn.sourceId);
                const tgtIdx = newIds.indexOf(conn.targetId);
                if (srcIdx === -1 || tgtIdx === -1) continue;
                const srcMirrors = symMirrorsByIndex[srcIdx]!;
                const tgtMirrors = symMirrorsByIndex[tgtIdx]!;
                const pairs = Math.min(srcMirrors.length, tgtMirrors.length);
                for (let i = 0; i < pairs; i += 1) {
                    const a = srcMirrors[i];
                    const b = tgtMirrors[i];
                    if (!a || !b || a.id === b.id) continue;
                    // Mirror counterparts of a node share its layer, so the
                    // mirrored connection rides the same layer as `conn`.
                    if (a.layerId !== b.layerId) continue;
                    mirrorConnections.push({
                        id: newId("conn"),
                        layerId: a.layerId,
                        sourceId: a.id,
                        targetId: b.id,
                        presetId: conn.presetId,
                    });
                }
            }
            // Bump trailing numbers on pasted standalone-shape labels too, so
            // copies don't collide with existing shapes or each other. Each
            // shape kind dedupes within its own label namespace.
            const relabel = <T extends { label: string }>(
                shapes: T[],
                used: Set<string>,
            ): T[] =>
                shapes.map((s) => {
                    const label = nextCopyLabel(s.label, used);
                    used.add(label);
                    return { ...s, label };
                });
            const labeledEllipses = relabel(
                newEllipses,
                new Set(prev.ellipses.map((el) => el.label)),
            );
            const labeledLines = relabel(
                newLines,
                new Set(prev.lines.map((l) => l.label)),
            );
            const labeledArcs = relabel(
                newArcs,
                new Set(prev.arcs.map((a) => a.label)),
            );
            const labeledRects = relabel(
                newRects,
                new Set(prev.rects.map((r) => r.label)),
            );
            const labeledPolygons = relabel(
                newPolygons,
                new Set(prev.polygons.map((p) => p.label)),
            );
            return {
                ...prev,
                nodes: [...prev.nodes, ...labeledNodes, ...created],
                connections: [
                    ...prev.connections,
                    ...pastedConnections,
                    ...mirrorConnections,
                ],
                ellipses: [...prev.ellipses, ...labeledEllipses],
                lines: [...prev.lines, ...labeledLines],
                arcs: [...prev.arcs, ...labeledArcs],
                rects: [...prev.rects, ...labeledRects],
                polygons: [...prev.polygons, ...labeledPolygons],
            };
        });
        // Select every pasted shape so the user can immediately reposition them.
        setSelectedNodeIds(newIds);
        setSelectedEllipseIds(newEllipseIds);
        setSelectedLineIds(newLineIds);
        setSelectedArcIds(newArcIds);
        setSelectedRectIds(newRectIds);
        setSelectedPolygonIds(newPolygonIds);
    }, [pushSnapshot, resolveActiveLayerId]);

    /** Copy appearance (color/size/shape) from the first selected node. */
    const copySelectedNodeStyle = useCallback(() => {
        // Use the first selected node in document order so the source is stable.
        const source = docRef.current.nodes.find((n) =>
            selectedNodeIds.includes(n.id),
        );
        if (!source) return;
        styleClipboardRef.current = {
            color: source.color,
            radius: source.radius,
            shape: source.shape,
            // Effects are document-level and shared; copy the references so the
            // target points at the same effects (matching node paste behavior).
            effectIds: [...source.effectIds],
        };
    }, [selectedNodeIds]);

    /** Apply copied appearance to all selected nodes. */
    const pasteNodeStyle = useCallback(() => {
        const style = styleClipboardRef.current;
        if (!style || selectedNodeIds.length === 0) return;
        const targets = new Set(
            docRef.current.nodes
                .filter((n) => selectedNodeIds.includes(n.id) && !n.spawnedFrom)
                .map((n) => n.id),
        );
        if (targets.size === 0) return;
        pushSnapshot();
        setDoc((prev) => {
            const liveEffects = new Set(prev.effects.map((e) => e.id));
            const effectIds = style.effectIds.filter((id) =>
                liveEffects.has(id),
            );
            let nodes = prev.nodes.map((n) =>
                targets.has(n.id) ? { ...n, ...style, effectIds } : n,
            );
            for (const nodeId of targets) {
                for (const hostId of hostIdsUsingTemplate(nodes, nodeId)) {
                    nodes = regenerateNodeSpawn(nodes, hostId);
                }
            }
            return { ...prev, nodes };
        });
    }, [selectedNodeIds, pushSnapshot]);

    /**
     * Apply the given appearance fields to every selected (non-clone) node in
     * one undo step, regenerating any spawn clones whose template changed. The
     * node analogue of `updateSelectedShapesStyle`; used by the multi-node
     * panel to recolor/restyle a whole selection at once.
     */
    const updateSelectedNodesStyle = useCallback(
        (partial: NodeStylePatch) => {
            const targets = new Set(
                docRef.current.nodes
                    .filter(
                        (n) => selectedNodeIds.includes(n.id) && !n.spawnedFrom,
                    )
                    .map((n) => n.id),
            );
            if (targets.size === 0) return;
            pushSnapshot();
            setDoc((prev) => {
                let nodes = prev.nodes.map((n) =>
                    targets.has(n.id) ? { ...n, ...partial } : n,
                );
                if (SPAWN_TEMPLATE_APPEARANCE_KEYS.some((k) => k in partial)) {
                    const hostIds = new Set<string>();
                    for (const id of targets) {
                        for (const hostId of hostIdsUsingTemplate(nodes, id)) {
                            hostIds.add(hostId);
                        }
                    }
                    for (const hostId of hostIds) {
                        nodes = regenerateNodeSpawn(nodes, hostId);
                    }
                }
                return { ...prev, nodes };
            });
        },
        [selectedNodeIds, pushSnapshot],
    );

    const duplicateNode = useCallback(
        (
            id: string,
            opts?: { alwaysMirror?: boolean },
        ): { id: string; mirrorIds: (string | null)[] } | null => {
            const cur = docRef.current;
            const src = cur.nodes.find((n) => n.id === id);
            if (!src || src.spawnedFrom) return null;
            pushSnapshot();
            const { id: _id, spawnedFrom: _spawnedFrom, ...rest } = src;
            void _id;
            void _spawnedFrom;
            const center = {
                x: viewBoxRef.current.w / 2,
                y: viewBoxRef.current.h / 2,
            };
            // Track labels handed out so each symmetric extra also gets a
            // distinct numbered label.
            const usedLabels = new Set(cur.nodes.map((n) => n.label));
            const takeLabel = (): string => {
                const label = nextCopyLabel(src.label, usedLabels);
                usedLabels.add(label);
                return label;
            };
            // Bump a trailing number on the label so "Node 1" duplicates to the
            // next free "Node N" (no-op for labels without a trailing number).
            const copy: DiagramNode = {
                ...structuredClone(rest),
                id: newId("node"),
                label: takeLabel(),
                spawnedFrom: null,
            };
            // Under symmetry, drop an independent copy at each mirrored
            // position (geometry match — no stored mirror link). `mirrorIds[i]`
            // is the id of the copy made for mirror position i, or `null` where
            // it was skipped; returned so an alt-drag can move the copy and its
            // mirrors together (matching the paste flow).
            //
            // In-place duplicates skip mirror positions already occupied by an
            // existing node (so a node in a symmetric pair doesn't stack a
            // duplicate on its counterpart). `alwaysMirror` (alt-drag) overrides
            // that: the copy starts atop the source, so its mirror positions sit
            // on the source's existing counterparts — we still want fresh,
            // independent mirror copies to drag away as a symmetric set.
            const extras: DiagramNode[] = [];
            // Each new node inherits the group memberships of the existing node
            // that sits at the position it was made for: the main copy starts
            // atop the source (so it inherits the source's groups), and each
            // mirror extra inherits the source's symmetric counterpart at that
            // mirror position. Maps new-node id -> the existing node id whose
            // groups it should join (or null when there's no node there).
            const groupSource: Record<string, string | null> = {
                [copy.id]: src.id,
            };
            const mirrorIds = mirrorPositions(
                copy.position,
                cur.symmetry,
                center,
            ).map((mp) => {
                const collidesWithNew = findNodeNearPosition(
                    [copy, ...extras],
                    mp,
                );
                const counterpart = findNodeNearPosition(cur.nodes, mp);
                const occupied =
                    collidesWithNew || (!opts?.alwaysMirror && counterpart);
                if (occupied) return null;
                const extra: DiagramNode = {
                    ...structuredClone(rest),
                    id: newId("node"),
                    label: takeLabel(),
                    position: { ...mp },
                    spawn: null,
                    spawnedFrom: null,
                };
                extras.push(extra);
                groupSource[extra.id] = counterpart?.id ?? null;
                return extra.id;
            });
            setDoc((prev) => {
                let nodes = [...prev.nodes, copy, ...extras];
                if (copy.spawn) nodes = regenerateNodeSpawn(nodes, copy.id);
                for (const ex of extras) {
                    if (ex.spawn) nodes = regenerateNodeSpawn(nodes, ex.id);
                }
                // A copy joins each group that contains the existing node it
                // was made from, so under symmetry a mirror extra lands in its
                // counterpart's groups rather than the source's.
                const groups = prev.groups.map((g) => {
                    const added = Object.entries(groupSource)
                        .filter(
                            ([, fromId]) =>
                                fromId != null && g.nodeIds.includes(fromId),
                        )
                        .map(([newNodeId]) => newNodeId);
                    return added.length
                        ? { ...g, nodeIds: [...g.nodeIds, ...added] }
                        : g;
                });
                return { ...prev, nodes, groups };
            });
            setSelectedNodeIds([copy.id]);
            return { id: copy.id, mirrorIds };
        },
        [pushSnapshot],
    );

    /**
     * Duplicate a single standalone ellipse in place (offset like a paste),
     * select the copy, and return its id. Returns null if the ellipse is gone.
     */
    const duplicateEllipse = useCallback(
        (id: string): string | null => {
            const src = docRef.current.ellipses.find((e) => e.id === id);
            if (!src) return null;
            pushSnapshot();
            const copy: DiagramEllipse = {
                ...structuredClone(src),
                id: newId("ellipse"),
                label: nextCopyLabel(
                    src.label,
                    docRef.current.ellipses.map((e) => e.label),
                ),
                center: roundVec2({
                    x: src.center.x + PASTE_OFFSET,
                    y: src.center.y + PASTE_OFFSET,
                }),
            };
            setDoc((prev) => ({ ...prev, ellipses: [...prev.ellipses, copy] }));
            setSelectedEllipseId(copy.id);
            return copy.id;
        },
        [pushSnapshot, setSelectedEllipseId],
    );

    /** Duplicate a single standalone line in place; returns the new line's id. */
    const duplicateLine = useCallback(
        (id: string): string | null => {
            const src = docRef.current.lines.find((l) => l.id === id);
            if (!src) return null;
            pushSnapshot();
            const offset = (v: Vec2): Vec2 =>
                roundVec2({ x: v.x + PASTE_OFFSET, y: v.y + PASTE_OFFSET });
            const copy: DiagramLine = {
                ...structuredClone(src),
                id: newId("line"),
                label: nextCopyLabel(
                    src.label,
                    docRef.current.lines.map((l) => l.label),
                ),
                start: offset(src.start),
                end: offset(src.end),
            };
            setDoc((prev) => ({ ...prev, lines: [...prev.lines, copy] }));
            setSelectedLineId(copy.id);
            return copy.id;
        },
        [pushSnapshot, setSelectedLineId],
    );

    /** Duplicate a single standalone arc in place; returns the new arc's id. */
    const duplicateArc = useCallback(
        (id: string): string | null => {
            const src = docRef.current.arcs.find((a) => a.id === id);
            if (!src) return null;
            pushSnapshot();
            const offset = (v: Vec2): Vec2 =>
                roundVec2({ x: v.x + PASTE_OFFSET, y: v.y + PASTE_OFFSET });
            const copy: DiagramArc = {
                ...structuredClone(src),
                id: newId("arc"),
                label: nextCopyLabel(
                    src.label,
                    docRef.current.arcs.map((a) => a.label),
                ),
                start: offset(src.start),
                end: offset(src.end),
            };
            setDoc((prev) => ({ ...prev, arcs: [...prev.arcs, copy] }));
            setSelectedArcId(copy.id);
            return copy.id;
        },
        [pushSnapshot, setSelectedArcId],
    );

    /** Duplicate a single standalone rect in place; returns the new rect's id. */
    const duplicateRect = useCallback(
        (id: string): string | null => {
            const src = docRef.current.rects.find((r) => r.id === id);
            if (!src) return null;
            pushSnapshot();
            const offset = (v: Vec2): Vec2 =>
                roundVec2({ x: v.x + PASTE_OFFSET, y: v.y + PASTE_OFFSET });
            const copy: DiagramRect = {
                ...structuredClone(src),
                id: newId("rect"),
                label: nextCopyLabel(
                    src.label,
                    docRef.current.rects.map((r) => r.label),
                ),
                start: offset(src.start),
                end: offset(src.end),
            };
            setDoc((prev) => ({ ...prev, rects: [...prev.rects, copy] }));
            setSelectedRectId(copy.id);
            return copy.id;
        },
        [pushSnapshot, setSelectedRectId],
    );

    /** Duplicate a single heat zone in place; returns the new zone's id. */
    const duplicateHeatZone = useCallback(
        (id: string): string | null => {
            const src = docRef.current.heatZones.find((z) => z.id === id);
            if (!src) return null;
            pushSnapshot();
            const offset = (v: Vec2): Vec2 =>
                roundVec2({ x: v.x + PASTE_OFFSET, y: v.y + PASTE_OFFSET });
            const copy: DiagramHeatZone = {
                ...structuredClone(src),
                id: newId("heatzone"),
                label: nextCopyLabel(
                    src.label,
                    docRef.current.heatZones.map((z) => z.label),
                ),
                start: offset(src.start),
                end: offset(src.end),
            };
            setDoc((prev) => ({
                ...prev,
                heatZones: [...prev.heatZones, copy],
            }));
            setSelectedHeatZoneId(copy.id);
            return copy.id;
        },
        [pushSnapshot, setSelectedHeatZoneId],
    );

    const duplicatePolygon = useCallback(
        (id: string): string | null => {
            const src = docRef.current.polygons.find((p) => p.id === id);
            if (!src) return null;
            pushSnapshot();
            const offset = (v: Vec2): Vec2 =>
                roundVec2({ x: v.x + PASTE_OFFSET, y: v.y + PASTE_OFFSET });
            const copy: DiagramPolygon = {
                ...structuredClone(src),
                id: newId("polygon"),
                label: nextCopyLabel(
                    src.label,
                    docRef.current.polygons.map((p) => p.label),
                ),
                points: src.points.map(offset),
            };
            setDoc((prev) => ({ ...prev, polygons: [...prev.polygons, copy] }));
            setSelectedPolygonId(copy.id);
            return copy.id;
        },
        [pushSnapshot, setSelectedPolygonId],
    );

    /** Delete all selected nodes (and their spawn clones). */
    const removeSelectedNodes = useCallback(() => {
        if (selectedNodeIds.length === 0) return;
        pushSnapshot();
        setDoc((prev) => {
            const removedIds = new Set<string>();
            for (const id of selectedNodeIds) {
                for (const nid of collectRemovedNodeIds(prev.nodes, id)) {
                    removedIds.add(nid);
                }
            }
            return finalizeNodeRemoval(prev, removedIds);
        });
        setSelectedNodeIds([]);
        setNodePanelOpen(false);
    }, [selectedNodeIds, pushSnapshot]);

    /** Copy every selected shape, then delete them all (Ctrl+X). */
    const cutSelectedNodes = useCallback(() => {
        if (
            selectedNodeIds.length === 0 &&
            selectedEllipseIds.length === 0 &&
            selectedLineIds.length === 0 &&
            selectedArcIds.length === 0 &&
            selectedRectIds.length === 0 &&
            selectedPolygonIds.length === 0
        ) {
            return;
        }
        copySelectedNodes();
        // Delete each selected shape type (each pushes its own history snapshot;
        // the copy above reads the live selection before any of them clear it).
        if (selectedNodeIds.length > 0) removeSelectedNodes();
        if (selectedEllipseIds.length > 0) removeSelectedEllipses();
        if (selectedLineIds.length > 0) removeSelectedLines();
        if (selectedArcIds.length > 0) removeSelectedArcs();
        if (selectedRectIds.length > 0) removeSelectedRects();
        if (selectedPolygonIds.length > 0) removeSelectedPolygons();
    }, [
        selectedNodeIds,
        selectedEllipseIds,
        selectedLineIds,
        selectedArcIds,
        selectedRectIds,
        selectedPolygonIds,
        copySelectedNodes,
        removeSelectedNodes,
        removeSelectedEllipses,
        removeSelectedLines,
        removeSelectedArcs,
        removeSelectedRects,
        removeSelectedPolygons,
    ]);

    /**
     * Align (or evenly distribute) the selected nodes. Spawn clones are
     * excluded (their position is derived from their host). Under active
     * symmetry, each aligned node's symmetric counterpart is dragged to track
     * its new position (matched by geometry, never auto-created).
     */
    const alignSelectedNodes = useCallback(
        (mode: AlignMode) => {
            const d = docRef.current;
            const isDistribute =
                mode === "distribute-h" || mode === "distribute-v";

            // Every selected element is reduced to an axis-aligned bounding box
            // and a `move(dx, dy)` translator, so alignment treats nodes,
            // ellipses, lines, arcs, and rectangles uniformly: their boxes'
            // edges / centers line up the same way regardless of shape. Nodes
            // additionally carry symmetric counterparts (handled at commit
            // time); the other shapes are plain geometry with no mirroring.
            interface AlignItem {
                /** Bounding box of the element in SVG user units. */
                minX: number;
                maxX: number;
                minY: number;
                maxY: number;
                /** Apply a translation to the element's defining points. */
                shift: (dx: number, dy: number) => void;
            }

            const nodeMoves: Array<{ id: string; pos: Vec2 }> = [];
            const ellipseMoves: Array<{ id: string; center: Vec2 }> = [];
            const lineMoves: Array<{ id: string; start: Vec2; end: Vec2 }> = [];
            const arcMoves: Array<{ id: string; start: Vec2; end: Vec2 }> = [];
            const rectMoves: Array<{ id: string; start: Vec2; end: Vec2 }> = [];
            const polygonMoves: Array<{ id: string; points: Vec2[] }> = [];

            const items: AlignItem[] = [];

            for (const n of d.nodes) {
                if (!selectedNodeIds.includes(n.id) || n.spawnedFrom) continue;
                const { x, y } = n.position;
                const r = n.radius;
                items.push({
                    minX: x - r,
                    maxX: x + r,
                    minY: y - r,
                    maxY: y + r,
                    shift: (dx, dy) =>
                        nodeMoves.push({
                            id: n.id,
                            pos: { x: x + dx, y: y + dy },
                        }),
                });
            }
            for (const el of d.ellipses) {
                if (!selectedEllipseIds.includes(el.id)) continue;
                const { x, y } = el.center;
                items.push({
                    minX: x - el.rx,
                    maxX: x + el.rx,
                    minY: y - el.ry,
                    maxY: y + el.ry,
                    shift: (dx, dy) =>
                        ellipseMoves.push({
                            id: el.id,
                            center: { x: x + dx, y: y + dy },
                        }),
                });
            }
            // Lines and arcs box from their endpoints (an arc's bulge is
            // ignored — its chord box keeps the math simple and matches how the
            // scale / rotate subtools treat them).
            const pushTwoPoint = (
                shape: { id: string; start: Vec2; end: Vec2 },
                out: Array<{ id: string; start: Vec2; end: Vec2 }>,
            ) => {
                const { start, end } = shape;
                items.push({
                    minX: Math.min(start.x, end.x),
                    maxX: Math.max(start.x, end.x),
                    minY: Math.min(start.y, end.y),
                    maxY: Math.max(start.y, end.y),
                    shift: (dx, dy) =>
                        out.push({
                            id: shape.id,
                            start: { x: start.x + dx, y: start.y + dy },
                            end: { x: end.x + dx, y: end.y + dy },
                        }),
                });
            };
            for (const l of d.lines) {
                if (selectedLineIds.includes(l.id)) pushTwoPoint(l, lineMoves);
            }
            for (const a of d.arcs) {
                if (selectedArcIds.includes(a.id)) pushTwoPoint(a, arcMoves);
            }
            for (const r of d.rects) {
                if (selectedRectIds.includes(r.id)) pushTwoPoint(r, rectMoves);
            }
            // Polygons box from the extent of all their vertices; shifting
            // translates every vertex by the same delta.
            for (const p of d.polygons) {
                if (!selectedPolygonIds.includes(p.id) || p.points.length === 0)
                    continue;
                const xs = p.points.map((pt) => pt.x);
                const ys = p.points.map((pt) => pt.y);
                const pts = p.points;
                items.push({
                    minX: Math.min(...xs),
                    maxX: Math.max(...xs),
                    minY: Math.min(...ys),
                    maxY: Math.max(...ys),
                    shift: (dx, dy) =>
                        polygonMoves.push({
                            id: p.id,
                            points: pts.map((pt) => ({
                                x: pt.x + dx,
                                y: pt.y + dy,
                            })),
                        }),
                });
            }

            if (items.length < (isDistribute ? 3 : 1)) return;

            // The edge / center coordinate each item is moved toward.
            const itemMinX = (it: AlignItem) => it.minX;
            const itemMaxX = (it: AlignItem) => it.maxX;
            const itemMinY = (it: AlignItem) => it.minY;
            const itemMaxY = (it: AlignItem) => it.maxY;
            const itemCenterX = (it: AlignItem) => (it.minX + it.maxX) / 2;
            const itemCenterY = (it: AlignItem) => (it.minY + it.maxY) / 2;

            if (items.length === 1 && !isDistribute) {
                // A lone element aligns to the canvas: its box edge touches the
                // matching canvas edge, or its box center sits on the centerline.
                const it = items[0]!;
                const { w, h } = viewBoxRef.current;
                let dx = 0;
                let dy = 0;
                if (mode === "left") dx = 0 - it.minX;
                else if (mode === "center-h") dx = w / 2 - itemCenterX(it);
                else if (mode === "right") dx = w - it.maxX;
                else if (mode === "top") dy = 0 - it.minY;
                else if (mode === "middle-v") dy = h / 2 - itemCenterY(it);
                else if (mode === "bottom") dy = h - it.maxY;
                it.shift(dx, dy);
            } else if (isDistribute) {
                // Spread the items' box centers evenly between the outermost two.
                const horiz = mode === "distribute-h";
                const center = horiz ? itemCenterX : itemCenterY;
                const sorted = [...items].sort((a, b) => center(a) - center(b));
                const lo = center(sorted[0]!);
                const hi = center(sorted[sorted.length - 1]!);
                const step = (hi - lo) / (sorted.length - 1);
                sorted.forEach((it, i) => {
                    const delta = lo + step * i - center(it);
                    it.shift(horiz ? delta : 0, horiz ? 0 : delta);
                });
            } else {
                // 2+ items align to the selection's combined bounding box.
                const minX = Math.min(...items.map(itemMinX));
                const maxX = Math.max(...items.map(itemMaxX));
                const minY = Math.min(...items.map(itemMinY));
                const maxY = Math.max(...items.map(itemMaxY));
                const cx = (minX + maxX) / 2;
                const cy = (minY + maxY) / 2;
                for (const it of items) {
                    let dx = 0;
                    let dy = 0;
                    if (mode === "left") dx = minX - it.minX;
                    else if (mode === "center-h") dx = cx - itemCenterX(it);
                    else if (mode === "right") dx = maxX - it.maxX;
                    else if (mode === "top") dy = minY - it.minY;
                    else if (mode === "middle-v") dy = cy - itemCenterY(it);
                    else if (mode === "bottom") dy = maxY - it.maxY;
                    it.shift(dx, dy);
                }
            }

            pushSnapshot();
            setDoc((prev) => {
                let nodes = prev.nodes;
                if (nodeMoves.length > 0) {
                    // Plan each aligned node move plus its symmetric
                    // counterparts. Explicit targets win over symmetry, so seed
                    // `claimed` with them first; last write per id wins.
                    const claimed = new Set<string>(
                        nodeMoves.map((m) => m.id),
                    );
                    const posById = new Map<string, Vec2>();
                    for (const { id, pos } of nodeMoves) {
                        for (const m of planSymmetricMove(
                            prev.nodes,
                            id,
                            pos,
                            claimed,
                        )) {
                            posById.set(m.id, m.pos);
                        }
                    }
                    nodes = applyNodeMoves(
                        prev.nodes,
                        Array.from(posById, ([id, pos]) => ({ id, pos })),
                    );
                }

                const apply2 = <T extends { id: string; start: Vec2; end: Vec2 }>(
                    list: Array<{ id: string; start: Vec2; end: Vec2 }>,
                    shapes: T[],
                ): T[] => {
                    if (list.length === 0) return shapes;
                    const byId = new Map(list.map((m) => [m.id, m]));
                    return shapes.map((s) => {
                        const m = byId.get(s.id);
                        return m
                            ? {
                                  ...s,
                                  start: roundVec2(m.start),
                                  end: roundVec2(m.end),
                              }
                            : s;
                    });
                };

                const ellipsesById = new Map(
                    ellipseMoves.map((m) => [m.id, m]),
                );
                return {
                    ...prev,
                    nodes,
                    ellipses:
                        ellipseMoves.length === 0
                            ? prev.ellipses
                            : prev.ellipses.map((e) => {
                                  const m = ellipsesById.get(e.id);
                                  return m
                                      ? { ...e, center: roundVec2(m.center) }
                                      : e;
                              }),
                    lines: apply2(lineMoves, prev.lines),
                    arcs: apply2(arcMoves, prev.arcs),
                    rects: apply2(rectMoves, prev.rects),
                    polygons:
                        polygonMoves.length === 0
                            ? prev.polygons
                            : (() => {
                                  const byId = new Map(
                                      polygonMoves.map((m) => [m.id, m]),
                                  );
                                  return prev.polygons.map((p) => {
                                      const m = byId.get(p.id);
                                      return m
                                          ? {
                                                ...p,
                                                points: m.points.map(roundVec2),
                                            }
                                          : p;
                                  });
                              })(),
                };
            });
        },
        [
            selectedNodeIds,
            selectedEllipseIds,
            selectedLineIds,
            selectedArcIds,
            selectedRectIds,
            selectedPolygonIds,
            planSymmetricMove,
            applyNodeMoves,
            pushSnapshot,
        ],
    );

    // --- Presets ----------------------------------------------------------

    const addPreset = useCallback(() => {
        pushSnapshot();
        const preset = createDefaultPreset(
            docRef.current.presets.length,
            docRef.current.strokeStyles[0]?.id ?? "",
        );
        setDoc((prev) => ({
            ...prev,
            presets: [...prev.presets, preset],
        }));
        // A newly defined preset becomes the one Connect/properties use next —
        // otherwise it's easy to define a preset and not notice it isn't active.
        setActivePresetId(preset.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updatePreset = useCallback(
        (id: string, partial: Partial<ConnectionPreset>) => {
            setDoc((prev) => ({
                ...prev,
                presets: prev.presets.map((p) =>
                    p.id === id ? { ...p, ...partial } : p,
                ),
            }));
        },
        [],
    );

    const removePreset = useCallback((id: string) => {
        pushSnapshot();
        setDoc((prev) => {
            if (prev.presets.length <= 1) return prev; // keep at least one
            const remaining = prev.presets.filter((p) => p.id !== id);
            const fallback = remaining[0]!.id;
            return {
                ...prev,
                presets: remaining,
                connections: prev.connections.map((c) =>
                    c.presetId === id ? { ...c, presetId: fallback } : c,
                ),
            };
        });
        setActivePresetId((cur) => {
            if (cur !== id) return cur;
            const remaining = docRef.current.presets.filter((p) => p.id !== id);
            return remaining[0]?.id ?? null;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- Stroke styles ----------------------------------------------------

    const addStrokeStyle = useCallback(() => {
        pushSnapshot();
        const style = createStrokeStyle(docRef.current.strokeStyles.length);
        setDoc((prev) => ({
            ...prev,
            strokeStyles: [...prev.strokeStyles, style],
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateStrokeStyle = useCallback(
        (id: string, partial: Partial<StrokeStyle>) => {
            setDoc((prev) => ({
                ...prev,
                strokeStyles: prev.strokeStyles.map((s) =>
                    s.id === id ? { ...s, ...partial } : s,
                ),
            }));
        },
        [],
    );

    const removeStrokeStyle = useCallback((id: string) => {
        pushSnapshot();
        setDoc((prev) => {
            if (prev.strokeStyles.length <= 1) return prev; // keep at least one
            const remaining = prev.strokeStyles.filter((s) => s.id !== id);
            // Presets must always reference a live style, so they fall back to
            // the first remaining one. Shapes/effects/outlines treat a style as
            // optional decoration, so they revert to their own plain stroke
            // (null) when the style they used is deleted.
            const presetFallback = remaining[0]!.id;
            const clearStyle = <T extends { strokeStyleId?: string | null }>(
                item: T,
            ): T =>
                item.strokeStyleId === id
                    ? { ...item, strokeStyleId: null }
                    : item;
            return {
                ...prev,
                strokeStyles: remaining,
                presets: prev.presets.map((p) =>
                    p.strokeStyleId === id
                        ? { ...p, strokeStyleId: presetFallback }
                        : p,
                ),
                ellipses: prev.ellipses.map(clearStyle),
                lines: prev.lines.map(clearStyle),
                arcs: prev.arcs.map(clearStyle),
                rects: prev.rects.map(clearStyle),
                polygons: prev.polygons.map(clearStyle),
                effects: prev.effects.map((eff) =>
                    eff.strokeStyleId === id
                        ? { ...eff, strokeStyleId: null }
                        : eff,
                ),
                groups: prev.groups.map((g) => ({
                    ...g,
                    properties: g.properties.map((prop) =>
                        prop.kind === "outline" && prop.strokeStyleId === id
                            ? { ...prop, strokeStyleId: null }
                            : prop,
                    ),
                })),
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- Layers -----------------------------------------------------------

    const addLayer = useCallback((): string => {
        const layer = createLayer(docRef.current.layers.length);
        pushSnapshot();
        setDoc((prev) => ({ ...prev, layers: [...prev.layers, layer] }));
        setActiveLayerId(layer.id);
        return layer.id;
    }, [pushSnapshot]);

    const updateLayer = useCallback(
        (id: string, partial: Partial<DiagramLayer>) => {
            // Name edits stream without snapshots; visibility/lock toggles are
            // discrete, so snapshot those for undo.
            if (partial.visible !== undefined || partial.locked !== undefined) {
                pushSnapshot();
            }
            setDoc((prev) => ({
                ...prev,
                layers: prev.layers.map((l) =>
                    l.id === id ? { ...l, ...partial } : l,
                ),
            }));
        },
        [pushSnapshot],
    );

    const removeLayer = useCallback(
        (id: string) => {
            // A document always keeps at least one layer.
            if (docRef.current.layers.length <= 1) return;
            pushSnapshot();
            setDoc((prev) => {
                const layers = prev.layers.filter((l) => l.id !== id);
                if (layers.length === prev.layers.length) return prev;
                const keep = <T extends { layerId: string }>(arr: T[]): T[] =>
                    arr.filter((x) => x.layerId !== id);
                // Drop the layer and everything on it (nodes take their spawn
                // clones and incident connections with them).
                const nodes = keep(prev.nodes);
                const liveNodeIds = new Set(nodes.map((n) => n.id));
                return {
                    ...prev,
                    layers,
                    nodes,
                    connections: keep(prev.connections).filter(
                        (c) =>
                            liveNodeIds.has(c.sourceId) &&
                            liveNodeIds.has(c.targetId),
                    ),
                    groups: keep(prev.groups),
                    ellipses: keep(prev.ellipses),
                    lines: keep(prev.lines),
                    arcs: keep(prev.arcs),
                    rects: keep(prev.rects),
                    polygons: keep(prev.polygons),
                    heatZones: keep(prev.heatZones),
                };
            });
            // Move the active layer off a just-deleted layer.
            setActiveLayerId((cur) =>
                cur === id
                    ? docRef.current.layers.find((l) => l.id !== id)?.id ?? cur
                    : cur,
            );
        },
        [pushSnapshot],
    );

    const moveLayer = useCallback(
        (id: string, direction: "up" | "down") => {
            pushSnapshot();
            setDoc((prev) => {
                const i = prev.layers.findIndex((l) => l.id === id);
                if (i === -1) return prev;
                // "up" = toward the front (later in the array = drawn on top);
                // "down" = toward the back.
                const j = direction === "up" ? i + 1 : i - 1;
                if (j < 0 || j >= prev.layers.length) return prev;
                const layers = [...prev.layers];
                [layers[i], layers[j]] = [layers[j]!, layers[i]!];
                return { ...prev, layers };
            });
        },
        [pushSnapshot],
    );

    const moveSelectionToLayer = useCallback(
        (layerId: string) => {
            if (!docRef.current.layers.some((l) => l.id === layerId)) return;
            const nodeSel = new Set(selectedNodeIds);
            const ellSel = new Set(selectedEllipseIds);
            const lineSel = new Set(selectedLineIds);
            const arcSel = new Set(selectedArcIds);
            const rectSel = new Set(selectedRectIds);
            const polySel = new Set(selectedPolygonIds);
            const zoneSel = new Set(selectedHeatZoneIds);
            if (
                nodeSel.size +
                    ellSel.size +
                    lineSel.size +
                    arcSel.size +
                    rectSel.size +
                    polySel.size +
                    zoneSel.size ===
                0
            ) {
                return;
            }
            pushSnapshot();
            setDoc((prev) => {
                const movedNodeIds = new Set<string>();
                const nodes = prev.nodes.map((n) => {
                    if (!nodeSel.has(n.id)) return n;
                    movedNodeIds.add(n.id);
                    return { ...n, layerId };
                });
                // A connection rides its endpoints' layer; if both endpoints
                // moved, the connection follows. (Selecting a node selects only
                // the node, but moving both ends keeps the link on-layer.)
                const connections = prev.connections.map((c) =>
                    movedNodeIds.has(c.sourceId) && movedNodeIds.has(c.targetId)
                        ? { ...c, layerId }
                        : c,
                );
                // A group follows when all its members moved (so it never spans
                // layers).
                const groups = prev.groups.map((g) =>
                    g.nodeIds.length > 0 &&
                    g.nodeIds.every((nid) => movedNodeIds.has(nid))
                        ? { ...g, layerId }
                        : g,
                );
                const restamp = <T extends { id: string; layerId: string }>(
                    arr: T[],
                    sel: Set<string>,
                ): T[] =>
                    arr.map((x) => (sel.has(x.id) ? { ...x, layerId } : x));
                return {
                    ...prev,
                    nodes,
                    connections,
                    groups,
                    ellipses: restamp(prev.ellipses, ellSel),
                    lines: restamp(prev.lines, lineSel),
                    arcs: restamp(prev.arcs, arcSel),
                    rects: restamp(prev.rects, rectSel),
                    polygons: restamp(prev.polygons, polySel),
                    heatZones: restamp(prev.heatZones, zoneSel),
                };
            });
        },
        [
            selectedNodeIds,
            selectedEllipseIds,
            selectedLineIds,
            selectedArcIds,
            selectedRectIds,
            selectedPolygonIds,
            selectedHeatZoneIds,
            pushSnapshot,
        ],
    );

    // --- Groups -----------------------------------------------------------

    const createGroupFromSelection = useCallback((nodeIds: string[]) => {
        if (nodeIds.length === 0) return;
        pushSnapshot();
        setDoc((prev) => {
            // Groups can't span layers: keep only the nodes sharing the first
            // selected node's layer, silently dropping any on other layers.
            const layerOf = (nid: string) =>
                prev.nodes.find((n) => n.id === nid)?.layerId;
            const groupLayerId = layerOf(nodeIds[0]!);
            if (!groupLayerId) return prev;
            const sameLayer = nodeIds.filter(
                (nid) => layerOf(nid) === groupLayerId,
            );
            if (sameLayer.length === 0) return prev;
            return {
                ...prev,
                groups: [
                    ...prev.groups,
                    createGroup(
                        sameLayer,
                        prev.groups.length,
                        groupLayerId,
                        prev.strokeStyles,
                    ),
                ],
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Apply the Group subtool's marquee to the document. `nodeIds` are the nodes
     * the rectangle grabbed; `mode` (the modifier held during the drag) picks the
     * operation — see `groupNodesFromMarquee` in the state interface. No-op when
     * the hit set can't satisfy the chosen mode (so an empty/ambiguous drag does
     * nothing rather than creating a spurious group). Selects the affected nodes.
     */
    const groupNodesFromMarquee = useCallback(
        (nodeIds: string[], mode: GroupToolMode) => {
            if (nodeIds.length === 0) return;
            const hit = new Set(nodeIds);
            pushSnapshot();
            setDoc((prev) => {
                const groupOf = (nid: string): NodeGroup | undefined =>
                    prev.groups.find((g) => g.nodeIds.includes(nid));

                if (mode === "remove") {
                    // For each hit node, find the LAST group it joined (the last
                    // group in document order whose members include it) and drop
                    // it from just that one.
                    const removeForNode = new Map<string, string>(); // nodeId -> groupId
                    for (let i = prev.groups.length - 1; i >= 0; i -= 1) {
                        const g = prev.groups[i]!;
                        for (const nid of hit) {
                            if (
                                !removeForNode.has(nid) &&
                                g.nodeIds.includes(nid)
                            ) {
                                removeForNode.set(nid, g.id);
                            }
                        }
                    }
                    if (removeForNode.size === 0) return prev;
                    const dropByGroup = new Map<string, Set<string>>();
                    for (const [nid, gid] of removeForNode) {
                        const set = dropByGroup.get(gid) ?? new Set<string>();
                        set.add(nid);
                        dropByGroup.set(gid, set);
                    }
                    return {
                        ...prev,
                        groups: prev.groups.map((g) => {
                            const dropIds = dropByGroup.get(g.id);
                            if (!dropIds) return g;
                            return {
                                ...g,
                                nodeIds: g.nodeIds.filter(
                                    (nid) => !dropIds.has(nid),
                                ),
                            };
                        }),
                    };
                }

                if (mode === "add") {
                    // Need exactly one grouped node and one ungrouped node: the
                    // ungrouped one joins the grouped one's group.
                    const grouped = nodeIds.filter((nid) => groupOf(nid));
                    const ungrouped = nodeIds.filter((nid) => !groupOf(nid));
                    if (grouped.length !== 1 || ungrouped.length !== 1) {
                        return prev;
                    }
                    const target = groupOf(grouped[0]!)!;
                    const addId = ungrouped[0]!;
                    // Groups can't span layers: only add the node when it shares
                    // the target group's layer.
                    const addLayerId = prev.nodes.find(
                        (n) => n.id === addId,
                    )?.layerId;
                    if (addLayerId !== target.layerId) return prev;
                    return {
                        ...prev,
                        groups: prev.groups.map((g) =>
                            g.id === target.id
                                ? { ...g, nodeIds: [...g.nodeIds, addId] }
                                : g,
                        ),
                    };
                }

                // mode === "create": new group from the ungrouped hit nodes
                // only — and only those sharing a single layer (groups can't
                // span layers; the first fresh node's layer wins).
                const fresh = nodeIds.filter((nid) => !groupOf(nid));
                if (fresh.length === 0) return prev;
                const layerOf = (nid: string) =>
                    prev.nodes.find((n) => n.id === nid)?.layerId;
                const groupLayerId = layerOf(fresh[0]!);
                if (!groupLayerId) return prev;
                const sameLayer = fresh.filter(
                    (nid) => layerOf(nid) === groupLayerId,
                );
                return {
                    ...prev,
                    groups: [
                        ...prev.groups,
                        createGroup(
                            sameLayer,
                            prev.groups.length,
                            groupLayerId,
                            prev.strokeStyles,
                        ),
                    ],
                };
            });
            setSelectedNodeIds(nodeIds);
        },
        [pushSnapshot],
    );

    /**
     * Add a single node to a specific group (the context menu's "Add to group"
     * submenu). No-op if the node is already a member, the group is gone, or the
     * node lives on a different layer (groups can't span layers).
     */
    const addNodeToGroup = useCallback(
        (nodeId: string, groupId: string) => {
            const d = docRef.current;
            const group = d.groups.find((g) => g.id === groupId);
            if (!group || group.nodeIds.includes(nodeId)) return;
            const nodeLayerId = d.nodes.find((n) => n.id === nodeId)?.layerId;
            if (!nodeLayerId || nodeLayerId !== group.layerId) return;
            pushSnapshot();
            setDoc((prev) => ({
                ...prev,
                groups: prev.groups.map((g) =>
                    g.id === groupId
                        ? { ...g, nodeIds: [...g.nodeIds, nodeId] }
                        : g,
                ),
            }));
        },
        [pushSnapshot],
    );

    /**
     * Remove a single node from a specific group (the context menu's "Remove
     * from group"). When `groupId` is omitted, the node is dropped from every
     * group it belongs to. No-op if the node isn't in the target group(s).
     */
    const removeNodeFromGroup = useCallback(
        (nodeId: string, groupId?: string) => {
            const inAny = docRef.current.groups.some(
                (g) =>
                    (!groupId || g.id === groupId) &&
                    g.nodeIds.includes(nodeId),
            );
            if (!inAny) return;
            pushSnapshot();
            setDoc((prev) => ({
                ...prev,
                groups: prev.groups.map((g) =>
                    (!groupId || g.id === groupId) &&
                    g.nodeIds.includes(nodeId)
                        ? {
                              ...g,
                              nodeIds: g.nodeIds.filter((id) => id !== nodeId),
                          }
                        : g,
                ),
            }));
        },
        [pushSnapshot],
    );

    const updateGroup = useCallback(
        (id: string, partial: Partial<NodeGroup>) => {
            setDoc((prev) => ({
                ...prev,
                groups: prev.groups.map((g) =>
                    g.id === id ? { ...g, ...partial } : g,
                ),
            }));
        },
        [],
    );

    const removeGroup = useCallback((id: string) => {
        pushSnapshot();
        setDoc((prev) => ({
            ...prev,
            groups: prev.groups.filter((g) => g.id !== id),
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- Stacking properties ---------------------------------------------

    // --- Effects ----------------------------------------------------------

    const addEffect = useCallback(
        (kind: EffectKind) => {
            pushSnapshot();
            const defaultStyle = docRef.current.strokeStyles[0]?.id ?? null;
            const effect = createEffect(
                kind,
                docRef.current.effects.length,
                defaultStyle,
            );
            setDoc((prev) => ({ ...prev, effects: [...prev.effects, effect] }));
        },
        [pushSnapshot],
    );

    const updateEffect = useCallback((id: string, partial: Partial<Effect>) => {
        setDoc((prev) => ({
            ...prev,
            effects: prev.effects.map((eff) =>
                eff.id === id ? ({ ...eff, ...partial } as Effect) : eff,
            ),
        }));
    }, []);

    const removeEffect = useCallback((id: string) => {
        pushSnapshot();
        setDoc((prev) => ({
            ...prev,
            effects: prev.effects.filter((eff) => eff.id !== id),
            // Detach the removed effect from every node that referenced it.
            nodes: prev.nodes.map((n) =>
                n.effectIds.includes(id)
                    ? { ...n, effectIds: n.effectIds.filter((e) => e !== id) }
                    : n,
            ),
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleNodeEffect = useCallback(
        (nodeId: string, effectId: string, on: boolean) => {
            pushSnapshot();
            setDoc((prev) => {
                let nodes = prev.nodes.map((n) => {
                    if (n.id !== nodeId) return n;
                    const has = n.effectIds.includes(effectId);
                    if (on === has) return n;
                    return {
                        ...n,
                        effectIds: on
                            ? [...n.effectIds, effectId]
                            : n.effectIds.filter((e) => e !== effectId),
                    };
                });
                for (const hostId of hostIdsUsingTemplate(nodes, nodeId)) {
                    nodes = regenerateNodeSpawn(nodes, hostId);
                }
                return { ...prev, nodes };
            });
        },
        [pushSnapshot],
    );

    // --- Patterns (document-level tiled-SVG fills) -------------------------

    const addPattern = useCallback(
        (name: string, svg: string): string => {
            pushSnapshot();
            const pattern = createPattern(
                name,
                svg,
                docRef.current.patterns.length,
            );
            setDoc((prev) => ({
                ...prev,
                patterns: [...prev.patterns, pattern],
            }));
            return pattern.id;
        },
        [pushSnapshot],
    );

    const updatePattern = useCallback(
        (id: string, partial: Partial<DiagramPattern>) => {
            setDoc((prev) => ({
                ...prev,
                patterns: prev.patterns.map((p) =>
                    p.id === id ? { ...p, ...partial } : p,
                ),
            }));
        },
        [],
    );

    const removePattern = useCallback((id: string) => {
        pushSnapshot();
        // Any fill pointing at the removed pattern falls back to no fill.
        const drop = (fill: Fill | null): Fill | null =>
            fill?.type === "pattern" && fill.patternId === id ? null : fill;
        setDoc((prev) => ({
            ...prev,
            patterns: prev.patterns.filter((p) => p.id !== id),
            rects: prev.rects.map((r) => ({ ...r, fill: drop(r.fill) })),
            polygons: prev.polygons.map((p) => ({ ...p, fill: drop(p.fill) })),
            ellipses: prev.ellipses.map((e) => ({ ...e, fill: drop(e.fill) })),
            groups: prev.groups.map((g) => ({
                ...g,
                properties: g.properties.map((prop) =>
                    prop.kind === "outline"
                        ? { ...prop, fill: drop(prop.fill) }
                        : prop,
                ),
            })),
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addGroupProperty = useCallback(
        (groupId: string, kind: GroupPropertyKind) => {
            pushSnapshot();
            const defaultStyle = docRef.current.strokeStyles[0]?.id ?? null;
            setDoc((prev) => ({
                ...prev,
                groups: prev.groups.map((g) => {
                    if (g.id !== groupId) return g;
                    const factory = PROPERTY_REGISTRY.group[kind].create;
                    return {
                        ...g,
                        properties: [...g.properties, factory(defaultStyle)],
                    };
                }),
            }));
        },
        [pushSnapshot],
    );

    const updateGroupProperty = useCallback(
        (groupId: string, index: number, partial: Partial<GroupProperty>) => {
            setDoc((prev) => ({
                ...prev,
                groups: prev.groups.map((g) => {
                    if (g.id !== groupId) return g;
                    const properties = g.properties.map((p, i) =>
                        i === index
                            ? ({ ...p, ...partial } as GroupProperty)
                            : p,
                    );
                    return { ...g, properties };
                }),
            }));
        },
        [],
    );

    const removeGroupProperty = useCallback(
        (groupId: string, index: number) => {
            pushSnapshot();
            setDoc((prev) => ({
                ...prev,
                groups: prev.groups.map((g) =>
                    g.id === groupId
                        ? {
                              ...g,
                              properties: g.properties.filter(
                                  (_, i) => i !== index,
                              ),
                          }
                        : g,
                ),
            }));
        },
        [pushSnapshot],
    );

    // --- Group outline presets --------------------------------------------

    const addGroupOutlinePreset = useCallback((): string => {
        pushSnapshot();
        const defaultStyle = docRef.current.strokeStyles[0]?.id ?? null;
        const preset = createGroupOutlinePreset(
            docRef.current.groupOutlinePresets.length,
            defaultStyle,
        );
        setDoc((prev) => ({
            ...prev,
            groupOutlinePresets: [...prev.groupOutlinePresets, preset],
        }));
        return preset.id;
    }, [pushSnapshot]);

    const updateGroupOutlinePreset = useCallback(
        (id: string, partial: Partial<GroupOutlinePreset>) => {
            setDoc((prev) => ({
                ...prev,
                groupOutlinePresets: prev.groupOutlinePresets.map((p) =>
                    p.id === id ? { ...p, ...partial } : p,
                ),
            }));
        },
        [],
    );

    const removeGroupOutlinePreset = useCallback(
        (id: string) => {
            pushSnapshot();
            setDoc((prev) => ({
                ...prev,
                groupOutlinePresets: prev.groupOutlinePresets.filter(
                    (p) => p.id !== id,
                ),
                // Unlink any group outline that referenced the removed preset,
                // so it falls back to its own (still-present) fields.
                groups: prev.groups.map((g) => ({
                    ...g,
                    properties: g.properties.map((prop) =>
                        prop.kind === "outline" && prop.presetId === id
                            ? { ...prop, presetId: null }
                            : prop,
                    ),
                })),
            }));
        },
        [pushSnapshot],
    );

    const applyOutlinePresetToAllGroups = useCallback(
        (presetId: string) => {
            pushSnapshot();
            const defaultStyle = docRef.current.strokeStyles[0]?.id ?? null;
            setDoc((prev) => ({
                ...prev,
                groups: prev.groups.map((g) => {
                    const hasOutline = g.properties.some(
                        (p) => p.kind === "outline",
                    );
                    const properties = hasOutline
                        ? g.properties.map((prop) =>
                              prop.kind === "outline"
                                  ? { ...prop, presetId }
                                  : prop,
                          )
                        : [
                              ...g.properties,
                              { ...createOutline(defaultStyle), presetId },
                          ];
                    return { ...g, properties };
                }),
            }));
        },
        [pushSnapshot],
    );

    // --- Symmetry ---------------------------------------------------------

    const setSymmetryAxis = useCallback(
        (axis: SymmetryAxis, on: boolean) => {
            pushSnapshot();
            setDoc((prev) => ({
                ...prev,
                symmetry: { ...prev.symmetry, [axis]: on },
            }));
        },
        [pushSnapshot],
    );

    /**
     * Toggle an editor-only center line guide. `guideX` is the vertical line down
     * the canvas's horizontal center; `guideY` is the horizontal line across its
     * vertical center. These draw no exported geometry, so they don't push undo
     * history.
     */
    const setSymmetryGuide = useCallback(
        (axis: "guideX" | "guideY", on: boolean) => {
            setDoc((prev) => ({
                ...prev,
                symmetry: { ...prev.symmetry, [axis]: on },
            }));
        },
        [],
    );

    /**
     * Snap the current multi-selection into exact symmetry across whichever
     * axes are active. For each active axis, selected elements are paired with
     * their nearest mirror-counterpart *within the selection*; for each pair the
     * element on the near side of the centerline (left for X, top for Y) is the
     * master, and its partner is moved to the master's exact reflection. Elements
     * with no counterpart in the selection are left untouched (no copies made).
     *
     * Pairing/snapping uses each element's centroid as the matching key and then
     * reflects its full defining geometry, so two-point shapes and polygons land
     * as true reflections, not just centroid-aligned. Axes are processed in
     * sequence (X then Y), composing into full symmetry when both are active.
     */
    const forceSymmetry = useCallback(() => {
        const sym = docRef.current.symmetry;
        const axes: SymmetryAxis[] = [];
        if (sym.x) axes.push("x");
        if (sym.y) axes.push("y");
        if (axes.length === 0) return;

        const nodeSel = new Set(selectedNodeIds);
        const ellSel = new Set(selectedEllipseIds);
        const lineSel = new Set(selectedLineIds);
        const arcSel = new Set(selectedArcIds);
        const rectSel = new Set(selectedRectIds);
        const polySel = new Set(selectedPolygonIds);
        const totalSelected =
            nodeSel.size +
            ellSel.size +
            lineSel.size +
            arcSel.size +
            rectSel.size +
            polySel.size;
        if (totalSelected < 2) return;

        const center = viewCenter();
        // Match tolerance: how far a centroid may sit from its partner's exact
        // mirror and still be treated as the same pair. Generous, since the whole
        // point is to snap together pairs that have drifted apart.
        const TOL = 24;

        // A uniform handle over every selected element: a centroid for pairing
        // plus getters/setters over its full defining geometry (the points that
        // must each be reflected for a true mirror snap).
        interface Movable {
            key: string; // `${kind}:${id}` — unique across types
            centroid: Vec2;
            points: Vec2[];
            set: (pts: Vec2[]) => void;
        }
        const movables: Movable[] = [];

        const nodePatch = new Map<string, Vec2>();
        const ellPatch = new Map<string, Vec2>();
        const linePatch = new Map<string, { start: Vec2; end: Vec2 }>();
        const arcPatch = new Map<string, { start: Vec2; end: Vec2 }>();
        const rectPatch = new Map<string, { start: Vec2; end: Vec2 }>();
        const polyPatch = new Map<string, Vec2[]>();

        const d = docRef.current;
        for (const n of d.nodes) {
            if (!nodeSel.has(n.id) || n.spawnedFrom) continue;
            movables.push({
                key: `node:${n.id}`,
                centroid: n.position,
                points: [n.position],
                set: (pts) => nodePatch.set(n.id, pts[0]!),
            });
        }
        for (const el of d.ellipses) {
            if (!ellSel.has(el.id)) continue;
            movables.push({
                key: `ellipse:${el.id}`,
                centroid: el.center,
                points: [el.center],
                set: (pts) => ellPatch.set(el.id, pts[0]!),
            });
        }
        const twoPoint = (
            id: string,
            kind: string,
            start: Vec2,
            end: Vec2,
            patch: Map<string, { start: Vec2; end: Vec2 }>,
        ): Movable => ({
            key: `${kind}:${id}`,
            centroid: { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 },
            points: [start, end],
            set: (pts) => patch.set(id, { start: pts[0]!, end: pts[1]! }),
        });
        for (const l of d.lines) {
            if (lineSel.has(l.id)) {
                movables.push(twoPoint(l.id, "line", l.start, l.end, linePatch));
            }
        }
        for (const a of d.arcs) {
            if (arcSel.has(a.id)) {
                movables.push(twoPoint(a.id, "arc", a.start, a.end, arcPatch));
            }
        }
        for (const r of d.rects) {
            if (rectSel.has(r.id)) {
                movables.push(twoPoint(r.id, "rect", r.start, r.end, rectPatch));
            }
        }
        for (const p of d.polygons) {
            if (!polySel.has(p.id) || p.points.length === 0) continue;
            const cx =
                p.points.reduce((s, v) => s + v.x, 0) / p.points.length;
            const cy =
                p.points.reduce((s, v) => s + v.y, 0) / p.points.length;
            movables.push({
                key: `polygon:${p.id}`,
                centroid: { x: cx, y: cy },
                points: p.points,
                set: (pts) => polyPatch.set(p.id, pts),
            });
        }

        const reflect = (v: Vec2, axis: SymmetryAxis): Vec2 =>
            axis === "x"
                ? roundVec2({ x: 2 * center.x - v.x, y: v.y })
                : roundVec2({ x: v.x, y: 2 * center.y - v.y });

        // Snap one pair per active axis. `points` for each movable are mutated in
        // place across axes so X+Y composes, and the final geometry is committed
        // once via `set` after all axes have been processed.
        const working = movables.map((m) => ({
            mov: m,
            pts: m.points.map((p) => ({ ...p })),
            changed: false,
        }));
        const byKey = new Map(working.map((w) => [w.mov.key, w]));

        for (const axis of axes) {
            const used = new Set<string>();
            for (const w of working) {
                if (used.has(w.mov.key)) continue;
                const cen = {
                    x: w.pts.reduce((s, p) => s + p.x, 0) / w.pts.length,
                    y: w.pts.reduce((s, p) => s + p.y, 0) / w.pts.length,
                };
                const target = reflect(cen, axis);
                // Nearest still-unpaired other movable to this one's mirror.
                let best: (typeof working)[number] | undefined;
                let bestD = TOL * TOL;
                for (const o of working) {
                    if (o === w || used.has(o.mov.key)) continue;
                    const oc = {
                        x:
                            o.pts.reduce((s, p) => s + p.x, 0) /
                            o.pts.length,
                        y:
                            o.pts.reduce((s, p) => s + p.y, 0) /
                            o.pts.length,
                    };
                    const dx = oc.x - target.x;
                    const dy = oc.y - target.y;
                    const dist2 = dx * dx + dy * dy;
                    if (dist2 < bestD) {
                        bestD = dist2;
                        best = o;
                    }
                }
                if (!best) continue;
                used.add(w.mov.key);
                used.add(best.mov.key);
                // Master = nearer the centerline along this axis (smaller coord);
                // its partner is snapped to the master's exact reflection.
                const wCoord = axis === "x" ? cen.x : cen.y;
                const bCen = {
                    x:
                        best.pts.reduce((s, p) => s + p.x, 0) /
                        best.pts.length,
                    y:
                        best.pts.reduce((s, p) => s + p.y, 0) /
                        best.pts.length,
                };
                const bCoord = axis === "x" ? bCen.x : bCen.y;
                const master = wCoord <= bCoord ? w : best;
                const slave = master === w ? best : w;
                slave.pts = master.pts.map((p) => reflect(p, axis));
                slave.changed = true;
            }
        }

        let any = false;
        for (const w of byKey.values()) {
            if (w.changed) {
                w.mov.set(w.pts);
                any = true;
            }
        }
        if (!any) return;

        pushSnapshot();
        setDoc((prev) => ({
            ...prev,
            nodes: prev.nodes.map((n) =>
                nodePatch.has(n.id)
                    ? { ...n, position: nodePatch.get(n.id)! }
                    : n,
            ),
            ellipses: prev.ellipses.map((el) =>
                ellPatch.has(el.id)
                    ? { ...el, center: ellPatch.get(el.id)! }
                    : el,
            ),
            lines: prev.lines.map((l) =>
                linePatch.has(l.id) ? { ...l, ...linePatch.get(l.id)! } : l,
            ),
            arcs: prev.arcs.map((a) =>
                arcPatch.has(a.id) ? { ...a, ...arcPatch.get(a.id)! } : a,
            ),
            rects: prev.rects.map((r) =>
                rectPatch.has(r.id) ? { ...r, ...rectPatch.get(r.id)! } : r,
            ),
            polygons: prev.polygons.map((p) =>
                polyPatch.has(p.id)
                    ? { ...p, points: polyPatch.get(p.id)! }
                    : p,
            ),
        }));
    }, [
        selectedNodeIds,
        selectedEllipseIds,
        selectedLineIds,
        selectedArcIds,
        selectedRectIds,
        selectedPolygonIds,
        viewCenter,
        pushSnapshot,
    ]);

    // --- Export -----------------------------------------------------------

    const buildExportSvgString = useCallback(() => {
        return renderDiagram2DToSvg(docRef.current, {
            presets: docRef.current.presets,
            outputWidth: viewBoxRef.current.w,
            outputHeight: viewBoxRef.current.h,
            background,
        });
    }, [background]);

    const canExport =
        doc.nodes.length > 0 ||
        doc.ellipses.length > 0 ||
        doc.lines.length > 0 ||
        doc.arcs.length > 0 ||
        doc.rects.length > 0 ||
        doc.polygons.length > 0;

    const handleDownload = useCallback(() => {
        const svgString = buildExportSvgString();
        if (!svgString) return;
        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "svg-diagram-2d.svg";
        a.click();
        URL.revokeObjectURL(url);
    }, [buildExportSvgString]);

    // --- Persistence ------------------------------------------------------

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            doc,
            viewBoxWidth,
            viewBoxHeight,
            background,
            snapEnabled,
            nodeBrushOptions,
            nodeInteractOptions,
            connectSelectionOptions,
            multiNodeBrushOptions,
            megaNodeBrushOptions,
        }),
        [
            doc,
            viewBoxWidth,
            viewBoxHeight,
            background,
            snapEnabled,
            nodeBrushOptions,
            nodeInteractOptions,
            connectSelectionOptions,
            multiNodeBrushOptions,
            megaNodeBrushOptions,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.doc && typeof values.doc === "object") {
            // Coerce backfills new fields and migrates legacy inline node
            // properties into document-level effects.
            const coerced = coerceDocument(values.doc);
            const next = {
                ...coerced,
                nodes: regenerateAllSpawns(coerced.nodes),
            };
            setDoc(next);
            setNodePanelOpen(false);
            setSelectedNodeIds([]);
            setSelectedEllipseIds([]);
            setSelectedLineIds([]);
            setSelectedArcIds([]);
            setSelectedRectIds([]);
            setSelectedPolygonIds([]);
            // The presets array may have been replaced (often with fresh ids,
            // e.g. on reset) — keep activePresetId pointing at a preset that
            // actually exists, instead of a stale id nothing matches.
            setActivePresetId((cur) =>
                cur && next.presets.some((p) => p.id === cur)
                    ? cur
                    : (next.presets[0]?.id ?? null),
            );
            // Likewise keep the active layer pointing at a layer that exists in
            // the loaded document.
            setActiveLayerId((cur) =>
                cur && next.layers.some((l) => l.id === cur)
                    ? cur
                    : (next.layers[0]?.id ?? ""),
            );
        }
        if (values.viewBoxWidth != null) {
            setViewBoxWidth(values.viewBoxWidth as number);
        }
        if (values.viewBoxHeight != null) {
            setViewBoxHeight(values.viewBoxHeight as number);
        }
        if (values.background != null) {
            setBackground(values.background as string);
        }
        if (typeof values.snapEnabled === "boolean") {
            setSnapEnabled(values.snapEnabled);
        }
        if (values.nodeBrushOptions && typeof values.nodeBrushOptions === "object") {
            // Merge onto defaults so a persisted blob from before a field was
            // added still yields a complete options object.
            setNodeBrushOptionsState({
                ...createNodeBrushOptions(),
                ...(values.nodeBrushOptions as Partial<NodeBrushOptions>),
            });
        }
        if (
            values.nodeInteractOptions &&
            typeof values.nodeInteractOptions === "object"
        ) {
            setNodeInteractOptionsState({
                ...createNodeInteractOptions(),
                ...(values.nodeInteractOptions as Partial<NodeInteractOptions>),
            });
        }
        if (
            values.connectSelectionOptions &&
            typeof values.connectSelectionOptions === "object"
        ) {
            setConnectSelectionOptionsState({
                ...createConnectSelectionOptions(),
                ...(values.connectSelectionOptions as Partial<ConnectSelectionOptions>),
            });
        }
        if (
            values.multiNodeBrushOptions &&
            typeof values.multiNodeBrushOptions === "object"
        ) {
            setMultiNodeBrushOptionsState({
                ...createMultiNodeBrushOptions(),
                ...(values.multiNodeBrushOptions as Partial<MultiNodeBrushOptions>),
            });
        }
        if (
            values.megaNodeBrushOptions &&
            typeof values.megaNodeBrushOptions === "object"
        ) {
            setMegaNodeBrushOptionsState({
                ...createMegaNodeBrushOptions(),
                ...(values.megaNodeBrushOptions as Partial<MegaNodeBrushOptions>),
            });
        }
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: TOOL_ID,
        defaultPreset,
        getPresetValues,
        applyPreset,
    });

    // --- Save / load project file -----------------------------------------
    // A project file is the same value bag the parameter-persistence layer uses
    // (doc + canvas settings), wrapped with a tool tag + version so the file is
    // self-describing and future migrations have something to branch on.

    const saveProjectToFile = useCallback(() => {
        const payload = {
            tool: TOOL_ID,
            version: 1,
            values: getPresetValues(),
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], {
            type: "application/json",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "svg-diagram-2d.json";
        a.click();
        URL.revokeObjectURL(url);
    }, [getPresetValues]);

    const loadProjectFromFile = useCallback(
        (file: File) => {
            const reader = new FileReader();
            reader.onload = () => {
                try {
                    const text =
                        typeof reader.result === "string" ? reader.result : "";
                    const parsed = JSON.parse(text) as {
                        tool?: string;
                        values?: Record<string, unknown>;
                    } & Record<string, unknown>;
                    if (parsed.tool && parsed.tool !== TOOL_ID) {
                        window.alert(
                            "That file isn't a 2D SVG Diagram project.",
                        );
                        return;
                    }
                    // Accept both the wrapped project shape and a bare value bag
                    // (e.g. a hand-edited preset export) for resilience.
                    const values =
                        parsed.values && typeof parsed.values === "object"
                            ? parsed.values
                            : parsed;
                    pushSnapshot();
                    applyPreset(values as Record<string, unknown>);
                } catch {
                    window.alert("Couldn't read that project file.");
                }
            };
            reader.readAsText(file);
        },
        [applyPreset, pushSnapshot],
    );

    const value = useMemo<SvgDiagram2DState>(
        () => ({
            doc,
            tool,
            setTool,
            selectedNodeIds,
            setSelectedNodeId,
            toggleNodeSelection,
            selectNodesInRect,
            selectInRect,
            clearSelection,
            connectSelectedNodes,
            connectSelectionOptions,
            setConnectSelectionOptions,
            clearConnectionsBetweenSelected,
            copySelectedNodes,
            cutSelectedNodes,
            pasteNodes,
            copySelectedNodeStyle,
            pasteNodeStyle,
            removeSelectedNodes,
            duplicateNode,
            nodePanelOpen,
            setNodePanelOpen,
            hoveredId,
            setHoveredId,
            alignSelectedNodes,
            connectSourceId,
            activePresetId,
            setActivePresetId,
            nodeBrushOptions,
            setNodeBrushOptions,
            applyNodeBrush,
            multiNodeBrushOptions,
            setMultiNodeBrushOptions,
            applyMultiNodeBrush,
            megaNodeBrushOptions,
            setMegaNodeBrushOptions,
            applyMegaNodeBrush,
            nodeInteractOptions,
            setNodeInteractOptions,
            applyNodeInteraction,
            addNodeAt,
            updateNode,
            moveNode,
            moveNodes,
            findNodeMirrorIds,
            removeNode,
            handleNodePick,
            addConnection,
            updateConnection,
            removeConnection,
            insertNodeOnConnection,
            selectedEllipseIds,
            setSelectedEllipseId,
            toggleEllipseSelection,
            addEllipse,
            updateEllipse,
            moveEllipse,
            moveEllipses,
            removeEllipse,
            removeSelectedEllipses,
            duplicateEllipse,
            selectedLineIds,
            setSelectedLineId,
            toggleLineSelection,
            addLine,
            updateLine,
            moveLine,
            moveLines,
            removeLine,
            removeSelectedLines,
            duplicateLine,
            selectedArcIds,
            setSelectedArcId,
            toggleArcSelection,
            addArc,
            updateArc,
            moveArc,
            moveArcs,
            removeArc,
            removeSelectedArcs,
            duplicateArc,
            selectedRectIds,
            setSelectedRectId,
            toggleRectSelection,
            addRect,
            updateRect,
            moveRect,
            moveRects,
            removeRect,
            removeSelectedRects,
            duplicateRect,
            selectedPolygonIds,
            setSelectedPolygonId,
            togglePolygonSelection,
            addPolygon,
            updatePolygon,
            movePolygon,
            movePolygons,
            removePolygon,
            removeSelectedPolygons,
            duplicatePolygon,
            selectedHeatZoneIds,
            setSelectedHeatZoneId,
            toggleHeatZoneSelection,
            addHeatZone,
            updateHeatZone,
            moveHeatZone,
            moveHeatZones,
            removeHeatZone,
            removeSelectedHeatZones,
            duplicateHeatZone,
            updateSelectedShapesStyle,
            updateSelectedNodesStyle,
            addPreset,
            updatePreset,
            removePreset,
            addStrokeStyle,
            updateStrokeStyle,
            removeStrokeStyle,
            activeLayerId,
            setActiveLayerId,
            addLayer,
            updateLayer,
            removeLayer,
            moveLayer,
            moveSelectionToLayer,
            createGroupFromSelection,
            groupNodesFromMarquee,
            addNodeToGroup,
            removeNodeFromGroup,
            updateGroup,
            removeGroup,
            revealGroup,
            groupRevealRequest: groupToReveal,
            revealConfigSection,
            configSectionRevealRequest: configSectionToReveal,
            addEffect,
            updateEffect,
            removeEffect,
            toggleNodeEffect,
            addPattern,
            updatePattern,
            removePattern,
            setNodeSpawn,
            updateNodeSpawn,
            addGroupProperty,
            updateGroupProperty,
            removeGroupProperty,
            addGroupOutlinePreset,
            updateGroupOutlinePreset,
            removeGroupOutlinePreset,
            applyOutlinePresetToAllGroups,
            setSymmetryAxis,
            setSymmetryGuide,
            forceSymmetry,
            undo,
            redo,
            canUndo: history.canUndo,
            canRedo: history.canRedo,
            pushSnapshot,
            viewBoxWidth,
            setViewBoxWidth,
            viewBoxHeight,
            setViewBoxHeight,
            background,
            setBackground,
            snapEnabled,
            setSnapEnabled,
            toggleSnapEnabled,
            refImage,
            setRefImageFromFile,
            clearRefImage,
            refImageVisible,
            setRefImageVisible,
            refImageOpacity,
            setRefImageOpacity,
            canExport,
            buildExportSvgString,
            handleDownload,
            getPresetValues,
            applyPreset,
            resetParameters,
            saveProjectToFile,
            loadProjectFromFile,
        }),
        [
            doc,
            tool,
            setTool,
            selectedNodeIds,
            setSelectedNodeId,
            toggleNodeSelection,
            selectNodesInRect,
            selectInRect,
            clearSelection,
            connectSelectedNodes,
            connectSelectionOptions,
            setConnectSelectionOptions,
            clearConnectionsBetweenSelected,
            copySelectedNodes,
            cutSelectedNodes,
            pasteNodes,
            copySelectedNodeStyle,
            pasteNodeStyle,
            removeSelectedNodes,
            duplicateNode,
            nodePanelOpen,
            setNodePanelOpen,
            hoveredId,
            setHoveredId,
            alignSelectedNodes,
            connectSourceId,
            activePresetId,
            nodeBrushOptions,
            setNodeBrushOptions,
            applyNodeBrush,
            multiNodeBrushOptions,
            setMultiNodeBrushOptions,
            applyMultiNodeBrush,
            megaNodeBrushOptions,
            setMegaNodeBrushOptions,
            applyMegaNodeBrush,
            nodeInteractOptions,
            setNodeInteractOptions,
            applyNodeInteraction,
            addNodeAt,
            updateNode,
            moveNode,
            moveNodes,
            findNodeMirrorIds,
            removeNode,
            handleNodePick,
            addConnection,
            updateConnection,
            removeConnection,
            insertNodeOnConnection,
            selectedEllipseIds,
            setSelectedEllipseId,
            toggleEllipseSelection,
            addEllipse,
            updateEllipse,
            moveEllipse,
            moveEllipses,
            removeEllipse,
            removeSelectedEllipses,
            duplicateEllipse,
            selectedLineIds,
            setSelectedLineId,
            toggleLineSelection,
            addLine,
            updateLine,
            moveLine,
            moveLines,
            removeLine,
            removeSelectedLines,
            duplicateLine,
            selectedArcIds,
            setSelectedArcId,
            toggleArcSelection,
            addArc,
            updateArc,
            moveArc,
            moveArcs,
            removeArc,
            removeSelectedArcs,
            duplicateArc,
            selectedRectIds,
            setSelectedRectId,
            toggleRectSelection,
            addRect,
            updateRect,
            moveRect,
            moveRects,
            removeRect,
            removeSelectedRects,
            duplicateRect,
            selectedPolygonIds,
            setSelectedPolygonId,
            togglePolygonSelection,
            addPolygon,
            updatePolygon,
            movePolygon,
            movePolygons,
            removePolygon,
            removeSelectedPolygons,
            duplicatePolygon,
            selectedHeatZoneIds,
            setSelectedHeatZoneId,
            toggleHeatZoneSelection,
            addHeatZone,
            updateHeatZone,
            moveHeatZone,
            moveHeatZones,
            removeHeatZone,
            removeSelectedHeatZones,
            duplicateHeatZone,
            updateSelectedShapesStyle,
            updateSelectedNodesStyle,
            addPreset,
            updatePreset,
            removePreset,
            addStrokeStyle,
            updateStrokeStyle,
            removeStrokeStyle,
            activeLayerId,
            setActiveLayerId,
            addLayer,
            updateLayer,
            removeLayer,
            moveLayer,
            moveSelectionToLayer,
            createGroupFromSelection,
            groupNodesFromMarquee,
            addNodeToGroup,
            removeNodeFromGroup,
            updateGroup,
            removeGroup,
            revealGroup,
            groupToReveal,
            revealConfigSection,
            configSectionToReveal,
            addEffect,
            updateEffect,
            removeEffect,
            toggleNodeEffect,
            addPattern,
            updatePattern,
            removePattern,
            setNodeSpawn,
            updateNodeSpawn,
            addGroupProperty,
            updateGroupProperty,
            removeGroupProperty,
            addGroupOutlinePreset,
            updateGroupOutlinePreset,
            removeGroupOutlinePreset,
            applyOutlinePresetToAllGroups,
            setSymmetryAxis,
            setSymmetryGuide,
            forceSymmetry,
            undo,
            redo,
            history.canUndo,
            history.canRedo,
            pushSnapshot,
            viewBoxWidth,
            viewBoxHeight,
            background,
            snapEnabled,
            toggleSnapEnabled,
            refImage,
            setRefImageFromFile,
            clearRefImage,
            refImageVisible,
            refImageOpacity,
            canExport,
            buildExportSvgString,
            handleDownload,
            getPresetValues,
            applyPreset,
            resetParameters,
            saveProjectToFile,
            loadProjectFromFile,
        ],
    );

    return (
        <SvgDiagram2DContext.Provider value={value}>
            {children}
        </SvgDiagram2DContext.Provider>
    );
}
